<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div class="tabulator-parent" ref="settingsContent">
      <div class="row">
        <div class="col-md-12">
          <h3><i class="fa fa-fw fa-globe"></i> {{ _('Storefront') }}</h3>
        </div>
      </div>
      <editor-tabs id="tabNavi" :tabs="tabList" :selected="selectedTab" :id-prefix="tabIdPrefix" v-on:select="tab" />
      <div class="tab-content">
        <transition-group name="fade">
          <div v-bind="tabPanel('frontpage')" class="tab-pane active" id="frontpage" v-if="isTabActive('frontpage')" key="frontpage">
            <div class="row">
              <div class="col-xs-12">
                <h3>{{ _('Main image') }}</h3>
              </div>
              <div class="col-sm-6 leftcol">
                <div class="form-group">
                  <label for="companyname">{{ _('Category to use in the image area') }}</label>
                  <select class="form-control" id="mainCategory" :placeholder="_('Select a category')" v-model="featuredCategory">
                    <option v-for="category in sortedCategories" :value="category.id">{{ category.name }}</option>
                  </select>
                </div>
              </div>
              <div class="col-sm-6 rightcol">
                <div class="form-group">
                  {{ _('The image area at the top of the front page may contain one or more images or products.') }}
                  {{ _('The images and products will be loaded from the product group and they will be shown in the order defined below.') }}
                </div>
              </div>
            </div>
            <div class="row" id="editMainCategoryContainer">
              <div class="col-sm-6 leftcol">
                <div class="form-group">
                  <label>{{ _('Slideshow order') }}</label>
                  <draggable v-model="slideshowItems" tag="ul" draggable=".list-group-item" :delay="100" :delayOnTouchOnly="true" :componentData="{ attrs: { class: 'o-sortable-slideshow list-group' }}">
                    <li v-for="id in slideshowItems" :class="'list-group-item' + (id === '*' ? ' list-group-item-warning' : '')">{{ getSlideshowItemName(id) }}</li>
                  </draggable>
                </div>
                <div class="form-group" v-if="featuredCategory">
                  <button type="button" class="btn btn-success" id="editMainCategory" v-on:click="editCategoryDialog('main')"><span class="fa fa-fw fa-folder-o"></span> {{ _('Edit category') }}</button>
                </div>
              </div>
              <div class="col-sm-6 rightcol">
                <div class="form-group">
                  {{ _('Set your preferred order of products and images in the slideshow. Any images found in the category will all be displayed at the selected position in the order they appear in the category.') }}
                </div>
              </div>
            </div>
            <hr />
            <div class="row" data-search="frontpage-search">
              <div class="col-xs-12">
                <h3>{{ _('Front page search') }}</h3>
              </div>
              <div class="col-sm-6 leftcol">
                <div class="checkbox">
                  <label>
                  <input type="checkbox" v-model="settings.frontpagesearch" true-value="1" false-value="0" />
                  {{ _('Display Search form on the Front page') }}
                  </label>
                </div>
                <template v-if="parseInt(settings.frontpagesearch)">
                  <div class="form-group">
                    <label for="frontpagesearchpos">{{ _('Position') }}</label>
                    <select class="form-control" id="frontpageSearchPos" v-model="settings.frontpagesearchpos" :placeholder="_('Select...')">
                      <option value="ontopofmainimage">{{ _('On top of the main image area') }}</option>
                      <option value="belowmainimage">{{ _('Below the main image area') }}</option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label for="frontpagesearchcategory">{{ _('Category to search from') }}</label>
                    <select class="form-control" id="frontpagesearchcategory" v-model="settings.frontpagesearchcategory" :placeholder="_('Select...')">
                      <option v-for="category in sortedCategories" :value="category.id">{{ category.name }}</option>
                      <option :value="null">{{ _('Not selected') }}</option>
                    </select>
                    <i>{{ _('If no category is selected, the search will target the entire shop.') }}</i>
                  </div>
                  <div class="checkbox">
                    <label>
                    <input type="checkbox" v-model="settings.frontpageavailabilitysearch" true-value="1" false-value="0" />
                    {{ _('Use availability search') }}
                    </label>
                  </div>
                  <div class="checkbox">
                    <label>
                    <input type="checkbox" v-model="settings.frontpagefulltextsearch" true-value="1" false-value="0" />
                    {{ _('Use full-text search') }}
                    </label>
                  </div>
                  <div class="checkbox">
                    <label>
                    <input type="checkbox" v-model="settings.frontpagesearchkeywords" true-value="1" false-value="0" />
                    {{ _('Use keywords') }}
                    </label>
                  </div>
                </template>
              </div>
              <div class="col-sm-6 leftcol">
                <template v-if="parseInt(settings.frontpagesearch)">
                  <div class="form-group languageversioned">
                    <label>{{ _('Search bar placeholder') }}</label>
                    <input v-model="settings.frontpagesearchmenulabel" type="text" class="form-control" :placeholder="_('Enter a custom placeholder for the search bar, defaults to Search')" />
                  </div>
                  <div class="form-group languageversioned">
                    <label>{{ _('Search button text') }}</label>
                    <input v-model="settings.frontpagesearchbuttontext" type="text" class="form-control" :placeholder="_('Enter a custom text for the search button, defaults to Search')" />
                  </div>
                </template>
              </div>
            </div>
            <hr/>
            <div class="row" data-search="frontpage-description">
              <div class="col-lg-6 leftcol">
                <h3>{{ _('Front page description') }}</h3>
                <div class="form-group languageversioned">
                  <label></label>
                  <editor v-model="settings.frontpagedescription" :embeds-enabled="true" :columns-enabled="true" />
                </div>
              </div>
              <div class="col-lg-6 rightcol">
                <div class="form-group">
                  <label for="metadescpos">{{ _('Position') }}</label>
                  <select class="form-control" id="metaDescPos" v-model="settings.metadescpos" :placeholder="_('Select...')">
                    <option value="ontopofmainimage">{{ _('On top of the main image area') }}</option>
                    <option value="belowmainimage">{{ _('Below the main image area') }}</option>
                    <option value="belowsearchform" v-if="parseInt(settings.frontpagesearch)">{{ _('Below the search form') }}</option>
                    <option value="abovefooter">{{ _('Above the footer') }}</option>
                    <option value="hidden">{{ _('Hidden') }}</option>
                  </select>
                </div>
              </div>
            </div>
            <hr />
            <div class="row">
              <div class="col-xs-12">
                <div class="form-group">
                  <h3>{{ _('Featured categories') }}</h3>
                </div>
              </div>
            </div>
            <div class="row" data-search="featured-categories">
              <div class="col-lg-6 leftcol">
                <draggable v-model="featuredCategoryGroups" handle=".featured-category-group-handle" filter=".featured-category-group-remove" :delay="100" :delayOnTouchOnly="true" @end="featuredCategoryGroupsReordered">
                <div class="form-group" v-for="(group, groupIndex) in featuredCategoryGroups" :key="'fcg-' + groupIndex">
                  <div class="panel panel-default">
                    <div class="panel-heading clearfix featured-category-group-handle" style="cursor: move;">
                      <span class="fa fa-fw fa-bars"></span>
                      <strong>{{ _('Category group') }} {{ groupIndex + 1 }}</strong>
                      <button type="button" class="btn btn-danger btn-xs pull-right featured-category-group-remove" v-on:click="removeFeaturedCategoryGroup(groupIndex)" v-if="featuredCategoryGroups.length > 1"><span class="fa fa-fw fa-trash"></span></button>
                    </div>
                    <div class="panel-body">
                      <div class="form-group">
                        <label>{{ _('Display') }}</label>
                        <select class="form-control" v-model="group.display" v-on:change="persistFeaturedCategoryGroups">
                          <option value="default">{{ _('Default') }}</option>
                          <option value="splitrows">{{ _('Split rows') }}</option>
                        </select>
                      </div>
                      <label>{{ _('Categories') }}</label>
                      <draggable v-model="group.links" tag="ul" draggable=".list-group-item" :delay="100" :delayOnTouchOnly="true" :componentData="{ attrs: { class: 'o-sortable-groups list-group' }}" @end="persistFeaturedCategoryGroups">
                        <li :class="'list-group-item' + (selectedFeaturedLink && selectedFeaturedLink.groupIndex === groupIndex && selectedFeaturedLink.linkIndex === linkIndex ? ' list-group-item-info' : '')" v-for="(link, linkIndex) in group.links" :key="'fcl-' + groupIndex + '-' + linkIndex" v-on:click="featuredLinkClicked(groupIndex, linkIndex)">
                          {{ getCategoryName(link.category_id) }}
                          <span class="text-muted" v-if="link.view"> · {{ productListViewLabel(link.view) }}</span>
                        </li>
                      </draggable>
                      <div class="form-group" style="margin-top:10px">
                        <button type="button" class="btn btn-success btn-sm" v-on:click="addFeaturedCategoryLinkDialog(groupIndex)"><span class="fa fa-fw fa-plus"></span> {{ _('Add Category') }}</button>
                        <button type="button" class="btn btn-info btn-sm" v-if="selectedFeaturedLink && selectedFeaturedLink.groupIndex === groupIndex" v-on:click="editFeaturedCategoryLinkDialog"><span class="fa fa-fw fa-pencil"></span> {{ _('Edit') }}</button>
                        <button type="button" class="btn btn-danger btn-sm" v-if="selectedFeaturedLink && selectedFeaturedLink.groupIndex === groupIndex" v-on:click="removeFeaturedCategoryLink"><span class="fa fa-fw fa-trash"></span> {{ _('Delete') }}</button>
                      </div>
                    </div>
                  </div>
                </div>
                </draggable>
                <div class="form-group">
                  <button type="button" class="btn btn-default" v-on:click="addFeaturedCategoryGroup"><span class="fa fa-fw fa-plus"></span> {{ _('Add category group') }}</button>
                </div>
              </div>
              <div class="col-lg-6 rightcol">
                <label></label>
                <p>{{ _('Use the front page to promote a collection of products that deserve special attention. Choose the categories you with to display on the front page and add them to the list.') }}</p>
                <p>{{ _('You can split featured categories into groups with different layouts. Default keeps the current category blocks; Split rows shows each category as an image and text row.') }}</p>
                <div class="form-group">
                  <label for="productLimitMode">{{ _('Maximum number of products to show on the front page from each category') }}</label>
                  <select class="form-control" id="productLimitMode" v-model="productLimitMode">
                    <option value="none">{{ _('No limit') }}</option>
                    <option value="hide">{{ _('Don\'t show products') }}</option>
                    <option value="limit">{{ _('Limited number') }}</option>
                  </select>
                </div>
                <div class="form-group" v-if="productLimitMode === 'limit'">
                  <input v-model="productLimitCount" type="number" min="1" step="1" data-android-inputmode="decimal" class="form-control" id="productLimit" :placeholder="_('Enter the maximum number of products')" />
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('search')" class="tab-pane active" id="search" v-if="isTabActive('search')" key="search">
            <div class="row">
              <div class="col-xs-12">
                <h3>{{ _('Search') }}</h3>
              </div>
              <div class="col-sm-6 leftcol">
                <div class="checkbox">
                  <label>
                  <input type="checkbox" v-model="settings.displaysearch" true-value="1" false-value="0" />
                  {{ _('Display Search in the main menu') }}
                  </label>
                </div>
                <div class="checkbox" data-search="full-text-search">
                  <label>
                  <input type="checkbox" v-model="settings.fulltextsearch" true-value="1" false-value="0" />
                  {{ _('Use full-text search') }}
                  </label>
                </div>
                <div class="checkbox" data-search="availability-search">
                  <label>
                  <input type="checkbox" v-model="settings.availabilitysearch" true-value="1" false-value="0" />
                  {{ _('Use availability search') }}
                  </label>
                </div>
                <h4>{{ _('Recommended results in the full-text search') }}</h4>
                <div class="form-group">
                  <label for="searchcategory">{{ _('Category to pull recommendations from') }}</label>
                  <select class="form-control" id="searchcategory" v-model="settings.searchcategory" :placeholder="_('Select...')">
                    <option v-for="category in sortedCategories" :value="category.id">{{ category.name }}</option>
                    <option :value="null">{{ _('Not selected') }}</option>
                  </select>
                </div>
                <h4>{{ _('Default view of the full-text search') }}</h4>
                <div class="radio">
                  <label>
                  <input type="radio" v-model="settings.defaultsearchview" value="list" />
                  {{ _('List') }}
                  </label>
                </div>
                <div class="radio">
                  <label>
                  <input type="radio" v-model="settings.defaultsearchview" value="map" />
                  {{ _('Map') }}
                  </label>
                </div>
                <h4>{{ _('Keywords') }}</h4>
                <div class="form-group" data-search="search-keywords">
                  <v-select :options="allSearchKeywords" v-model="keywordToAdd" v-on:option:selected="addKeyword" :taggable="true" label="title" :keys="['title']" :placeholder="_('Type to search or add keywords')">
                    <template v-slot:no-options="{ searching }">
                      <template v-if="searching">
                        {{ _('No results found.') }}
                      </template>
                      <template v-else>{{ _('Type to search or add keywords') }}</template>
                    </template>
                  </v-select>
                </div>
                <div class="form-group" v-if="searchKeywords.length">
                  <div id="tagsList" class="button-col">
                    <span v-for="keyword in searchKeywords" class="btn btn-info" v-on:click="removeKeyword(keyword)"><i class="fa fa-fw fa-close"></i> {{ keyword }}</span>
                  </div>
                  <div>({{ getVersionName(version) }})</div>
                </div>
              </div>
              <div class="col-sm-6 leftcol">
                <div class="form-group languageversioned" data-search="search-bar-placeholder">
                  <label>{{ _('Search bar placeholder') }}</label>
                  <input v-model="settings.searchmenulabel" type="text" class="form-control" :placeholder="_('Enter a custom label for the Search action in the top menu, defaults to Search')" />
                </div>
                <div class="form-group languageversioned" data-search="search-results-title">
                  <label>{{ _('Search results title') }}</label>
                  <input v-model="settings.searchresultstitle" type="text" class="form-control" :placeholder="_('Enter an optional title for the Search results list view')" />
                </div>
                <div class="form-group languageversioned">
                  <label>{{ _('Search results subtitle') }}</label>
                  <input v-model="settings.searchresultssubtitle" type="text" class="form-control" :placeholder="_('Enter an optional subtitle for the Search results list view')" />
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('menu')" class="tab-pane active" id="menu" v-if="isTabActive('menu')" key="menu">
            <div class="row">
              <div class="col-md-4">
                <h3>{{ _('Categories not displayed in the Storefront menu') }}</h3>
                <hr />
                <draggable group="categories" v-model="privateCategories" tag="ul" draggable=".list-group-item" :delay="100" :delayOnTouchOnly="true" :componentData="{ attrs: { class: 'o-sortable-allproductgroups sortable-list list-group', style: 'min-height: 300px' }}">
                  <li v-for="category in privateCategories" :class="displayedCategoryClass(category)" v-on:click="toggleDisplayedCategory(category)">{{ category.name }}</li>
                </draggable>
                <button v-if="privateCategories && privateCategories.length" type="button" class="btn btn-info" v-on:click="addAllCategoriesToStorefront"><i class="fa fa-fw fa-plus-square"></i> {{ _('Add all categories to the Storefront menu') }}</button>
              </div>
              <div class="col-md-4">
                <h3>{{ _('Categories displayed in the Storefront menu') }}</h3>
                <hr />
                <draggable group="categories" v-model="displayedCategories" tag="ul" draggable=".list-group-item" :delay="100" :delayOnTouchOnly="true" :componentData="{ attrs: { class: 'o-sortable-productgroups sortable-list list-group', style: 'min-height: 300px' }}">
                  <li v-for="category in displayedCategories" :class="displayedCategoryClass(category)" v-on:click="toggleDisplayedCategory(category)">{{ category.name }}
                    <span class="pull-right">
                      <template v-for="version in storefrontversions">
                        &nbsp;<button type="button" :class="itemVersionClass(category, version)" v-on:click.stop="toggleItemVersion(category, version)">{{ version.split('_')[0] }}</button>
                      </template>
                    </span>
                  </li>
                </draggable>
                <button v-if="displayedCategories && displayedCategories.length" type="button" class="btn btn-info" v-on:click="sortDisplayedCategories"><i class="fa fa-fw fa-sort-alpha-asc"></i> {{ _('Sort categories alphabetically') }}</button>
              </div>
              <div id="productsorter" class="col-md-4" v-if="selectedDisplayedCategory">
                <h3><span id="selectedgroupname">{{ selectedCategoryName }}</span><button class="btn btn-success pull-right" type="button" id="editSelectedCategory" v-on:click="editCategoryDialog('products')"><span class="fa fa-fw fa-folder-o"></span> {{ _('Edit') }}</button></h3>
                <hr />
                <draggable group="products" v-model="topLevelSelectedCategoryProducts" tag="ul" draggable=".list-group-item" :delay="100" :delayOnTouchOnly="true" :componentData="{ attrs: { class: 'o-sortable-products sortable-list sorted-products list-group' }}" v-on:change="productOrderChanged">
                  <template v-for="product in topLevelSelectedCategoryProducts">
                    <li v-if="product.id === 'other'" class="list-group-item list-group-item-warning"><a href="javascript:;" v-on:click="toggleOtherProducts" :style="(showOtherProducts ? 'display: inline-block; margin-bottom: 10px': '')"><i :class="'fa fa-fw fa-folder' + (showOtherProducts ? '-open' : '')"></i> {{ _('Other products in alphabetical order') }}</a>
                      <draggable group="products" v-model="defaultSelectedCategoryProducts" tag="ul" draggable=".list-group-item" :delay="100" :delayOnTouchOnly="true" :componentData="{ attrs: { class: 'o-sortable-products-inner sortable-list other-products list-group' + (showOtherProducts ? '' : ' collapse') }}">
                        <li class="list-group-item" v-for="product in defaultSelectedCategoryProducts" :key="product.id">{{ product.name }}<template v-if="product.shopid !== shopId"> ({{ product.shopid }})</template>
                          <span class="pull-right">
                            <template v-for="version in storefrontversions">
                              &nbsp;<button :disabled="product.shopid !== shopId" type="button" :class="itemVersionClass(product, version)" v-on:click.stop="toggleProductVersion(product, version)">{{ version.split('_')[0] }}</button>
                            </template>
                          </span>
                        </li>
                        <li class="list-group-item" v-if="!defaultSelectedCategoryProducts.length" key="none"><i>{{ _('No other products') }}</i></li>
                      </draggable>
                    </li>
                    <li v-else class="list-group-item" :key="product.id">{{ product.name }}<template v-if="product.shopid !== shopId"> ({{ product.shopid }})</template>
                      <span class="pull-right">
                        <template v-for="version in storefrontversions">
                          &nbsp;<button :disabled="product.shopid !== shopId" type="button" :class="itemVersionClass(product, version)" v-on:click.stop="toggleProductVersion(product, version)">{{ version.split('_')[0] }}</button>
                        </template>
                      </span>
                    </li>
                  </template>
                </draggable>
                <div>{{ _('Set all products as visible in Storefront versions') }}:</div>
                <template v-for="version in storefrontversions">
                  &nbsp;<button type="button" class="btn btn-xs btn-success" v-on:click.stop="enableVersionForAllProducts(version)">{{ version.split('_')[0] }}</button>
                </template>
              </div>
            </div>
            <div class="row">
              <div class="col-xs-12">
                <hr />
                <div class="form-group">
                  <p>
                    {{ _('Categories selected to the Storefront make up the top level menu structure. The first list contains all categories that are not yet displayed in the Storefront. Drag and drop categories from that list to the list of displayed categories. You can change the order of products in each category after selecting the category by clicking its name and then dragging the products into the desired order.') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('pages')" class="tab-pane active" id="pages" v-if="false && isTabActive('pages')" key="pages">
            <div class="row">
              <div class="col-xs-6">
                <h3>{{ _('Pages') }}</h3>
              </div>
              <div class="col-xs-6">
                <button type="button" class="btn btn-success pull-right new-page" v-on:click="addPageDialog"><i class="fa fa-fw fa-plus"></i> {{ _('New page') }}</button>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6 col-sm-12 filtercol">
                <div class="form-group">
                  <div class="input-group">
                    <input class="form-control" id="searchField" type="text" v-model="search" :placeholder="_('Keyword search')" />
                    <span class="input-group-btn">
                      <button type="button" class="btn btn-success"><i class="fa fa-fw fa-search"></i></button>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <Datatable :url="pagesUrl" :search="search" :options="pagesTableOptions" ref="datatablePages" v-on:data="tableEvent" />
          </div>
          <div v-bind="tabPanel('widgets')" class="tab-pane active" id="widgets" v-if="isTabActive('widgets')" key="widgets">
            <div class="row">
              <div class="col-xs-6">
                <h3><i class="fa fa-fw fa-object-group"></i> {{ _('Widgets') }}</h3>
              </div>
              <div class="col-xs-6">
                <button type="button" class="btn btn-success pull-right new-widget" v-on:click="addWidgetDialog"><i class="fa fa-fw fa-plus"></i> {{ _('New widget') }}</button>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6 col-sm-12 filtercol">
                <div class="form-group">
                  <div class="input-group">
                    <input class="form-control" id="searchField" type="text" v-model="search" :placeholder="_('Keyword search')" />
                    <span class="input-group-btn">
                      <button type="button" class="btn btn-success"><i class="fa fa-fw fa-search"></i></button>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="alert alert-warning unpublished-widgets-alert" v-if="affectedProductWidgets.length">
              <span>
                <i class="fa fa-fw fa-exclamation-triangle"></i>
                {{ _('Some product widgets reference products that are not published for the current language and will not display.') }}
              </span>
              <button type="button" class="btn btn-sm btn-warning" v-on:click="publishTargetProducts" v-if="publishableAffectedProducts.length">{{ _('Publish products') }}</button>
            </div>
            <div class="alert alert-warning unpublished-widgets-alert" v-if="affectedCategoryWidgets.length">
              <span>
                <i class="fa fa-fw fa-exclamation-triangle"></i>
                {{ _('Some category widgets reference categories that are not visible in widgets for the current language and will not display.') }}
              </span>
              <button type="button" class="btn btn-sm btn-warning" v-on:click="allowCategoriesInWidgets" v-if="allowableAffectedCategories.length">{{ _('Allow to display in widgets') }}</button>
            </div>
            <Datatable :url="widgetsUrl" :search="search" :options="widgetsTableOptions" ref="datatableWidgets" v-on:data="tableEvent" />
          </div>
          <div v-bind="tabPanel('metadata')" class="tab-pane active" id="metadata" v-if="isTabActive('metadata')" key="metadata">
            <div class="row">
              <div class="col-lg-6 leftcol">
                <h3>{{ _('Front page') }}</h3>
                <div class="form-group languageversioned">
                  <label for="frontpagepagetitle">{{ _('Page title') }}</label>
                  <input type="text" class="form-control" id="frontpagepagetitle" v-model="settings.frontpagepagetitle" />
                </div>
                <div class="form-group languageversioned">
                  <label for="metadescription">{{ _('Short description') }}</label>
                  <textarea id="metaDescription" class="form-control" v-model="settings.metadescription" rows="4" wrap="virtual"></textarea>
                </div>
                <div class="form-group languageversioned">
                  <label for="newSeoTag">{{ _('SEO Tags') }}</label>
                  <div class="input-group">
                    <input class="form-control wide textField" id="newSeoTag" type="text" v-model="newSeoTag" />
                    <span class="input-group-btn">
                      <button type="button" class="btn btn-info" id="addSeoTagButton" v-on:click="addTag"><i class="fa fa-fw fa-plus"></i> {{ _('Add') }}</button>
                    </span>
                  </div>
                </div>
                <div class="form-group">
                  <div id="seoTagsList" class="button-col">
                    <span v-for="tag in seoTags" class="btn btn-info" v-on:click="removeTag(tag)"><i class="fa fa-fw fa-close"></i> {{ tag }}</span>
                  </div>
                </div>
              </div>
              <div class="col-lg-6 rightcol">
                <h3>{{ _('Google') }}</h3>
                <div class="form-group" data-search="google-analytics">
                  <label for="analyticsapikey">{{ _('Google Analytics key') }}</label>
                  <input class="form-control" v-model="settings.analyticsapikey" type="text" />
                  <p class="text-info"><i class="fa fa-fw fa-info"></i> {{ _('Optional, can be used to track and collect statistics of product embeds.') }}</p>
                  <p class="text-info" v-if="!(settings.cookiebotid || parseInt(settings.customcookiemanager)) && isFeatureEnabled('cookiebot')"><i class="fa fa-fw fa-info"></i> {{ _('Cookie management is required to use this functionality.') }}</p>
                </div>
                <div class="form-group" data-search="google-tag-manager">
                  <label for="gtmkey">{{ _('Google Tag Manager container ID') }}</label>
                  <input class="form-control" v-model="settings.gtmkey" type="text" />
                  <p class="text-info"><i class="fa fa-fw fa-info"></i> {{ _('Optional, can be used to track and collect statistics of product embeds.') }}</p>
                  <p class="text-info" v-if="!(settings.cookiebotid || parseInt(settings.customcookiemanager)) && isFeatureEnabled('cookiebot')"><i class="fa fa-fw fa-info"></i> {{ _('Cookie management is required to use this functionality.') }}</p>
                </div>
                <div class="form-group" data-search="google-maps-api-key">
                  <label for="calendarauthcode">{{ _('Google Maps API key') }}</label>
                  <input class="form-control" v-model="settings.googlemapsapikey" type="text" />
                  <p class="text-info"><i class="fa fa-fw fa-info"></i> {{ _('Required when using Google Maps.') }}</p>
                  <p class="text-info" v-if="!(settings.cookiebotid || parseInt(settings.customcookiemanager)) && isFeatureEnabled('cookiebot')"><i class="fa fa-fw fa-info"></i> {{ _('Cookie management is required to use this functionality.') }}</p>
                </div>
                <div class="form-group">
                  <label for="metadescription">{{ _('Google Site Verification') }}</label>
                  <input type="text" class="form-control" id="googleSiteVerification" v-model="settings.googlesiteverification" />
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('settings')" class="tab-pane active" id="settings" v-if="isTabActive('settings')" key="settings">
            <div class="row">
              <div class="col-lg-6 leftcol">
                <h3>{{ _('Other settings') }}</h3>
                <div class="form-group">
                <label for="smartcrop">{{ _('Smart Crop') }}</label><br />
                  <select class="form-control" id="smartCrop" v-model="settings.smartcrop">
                    <option value="full">{{ _('Fully enabled with face detection') }}</option>
                    <option value="nofaces">{{ _('Enabled without face detection') }}</option>
                    <option value="disable">{{ _('Disabled') }}</option>
                  </select>
                </div>
                <p>{{ _('Johku Storefront will automatically attempt to crop and resize product and category images to include faces and other detected interesting features.') }}
                  {{ _('You can limit or completely disable this, but changing the setting will only affect images uploaded after the change.') }}</p>
                <div class="form-group languageversioned">
                  <label for="upsellTitle">{{ _('Upsell products title') }}</label>
                  <input type="text" class="form-control" id="upsellTitle" :placeholder="_('Enter an optional title for the upsell products in the product page')" v-model="settings.upselltitle" />
                </div>
                <div class="form-group languageversioned">
                  <label for="useACodeText">{{ _('Use a code button description') }}</label>
                  <input type="text" class="form-control" id="useACodeText" :placeholder="_('Enter an optional description for the Use a code button on the order page')" v-model="settings.useacodetext" />
                </div>
              </div>
              <div class="col-lg-6 rightcol">
                <h3>{{ _('Shop contact information') }}</h3>
                <div class="form-group languageversioned">
                  <label for="shopMarketingname">{{ _('Company marketing name') }}</label>
                  <input type="text" class="form-control" id="shopMarketingname" :placeholder="_('Enter the company marketing name')" v-model="settings.marketingname" />
                </div>
                <p>{{ _('If you want to use a separate marketing name for your company that is different from the company name, you can set it here.') }}</p>
                <div class="form-group">
                  <label for="shopPhone">{{ _('Shop phone') }}</label>
                  <input type="text" class="form-control" id="shopPhone" :placeholder="_('Enter the shop phone number')" v-model="settings.shopphone" />
                </div>
                <div class="form-group">
                  <label for="shopEmail">{{ _('Shop email') }}</label>
                  <input type="email" class="form-control" id="shopEmail" :placeholder="_('Enter the shop email address')" v-model="settings.shopemail" />
                </div>
                <div class="form-group">
                  <label for="hideShopFromLocal">{{ _('Merchant information') }}</label>
                  <select class="form-control" id="hideShopFromLocal" v-model="settings.hideshopfromlocal">
                    <option value="0">{{ _('Display merchant information on all products') }}</option>
                    <option value="1">{{ _('Don\'t display merchant information for local products') }}</option>
                    <option value="2">{{ _('Don\'t display merchant information on any product') }}</option>
                  </select>
                </div>
                <hr />
                <div class="form-group languageversioned">
                  <label for="shopTitle">{{ _('Shop title') }}</label>
                  <input name="shoptitle" type="text" class="form-control" id="shopTitle" :placeholder="_('Enter the shop title')" v-model="settings.shoptitle" />
                </div>
                <p>{{ _('If you want to display a shop title that is different from the company name, you can set it here.') }}</p>
              </div>
            </div>
            <div>
              <h3>{{ _('Domain settings') }}</h3>
            </div>
            <div class="row">
              <div class="col-lg-6 leftcol">
                <div class="form-group">
                  <label for="domainName">{{ _('Domain name') }}</label>
                  <input type="text" class="form-control" id="domainName" :placeholder="shopId + '.johku.com'" v-model="settings.shopdomain" />
                </div>
              </div>
              <div class="col-lg-6 leftcol">
                <p>{{ _('The default address of your store is') }} <strong>https://{{ shopId }}.johku.com/</strong>. {{ _('You can optionally use a custom domain name. Enter the domain name in the adjacent field and update the following details to your name servers.') }}</p>
              </div>
            </div>
            <div class="row">
              <div class="col-xs-12" id="domainSettings" v-if="settings.shopdomain">
                <h4 class="domainTitle">{{ gdn(settings.shopdomain) }}</h4>
                <div class="panel panel-default">
                  <div class="panel-body">
                    <table class="table table-condensed">
                      <thead>
                      <tr>
                        <th>Name/Host/Alias</th>
                        <th>Time to Live (TTL)</th>
                        <th>Record Type</th>
                        <th>Value/Answer/Destination</th>
                      </tr>
                      </thead>
                      <tbody v-html="getDomainRows(settings.shopdomain)">
                      </tbody>
                    </table>
                  </div>
                </div>
                <div class="well">{{ _('Also remove any existing A and AAAA records pointing to these names.') }}</div>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-6 leftcol">
                <div class="form-group" data-search="maps-api-key">
                  <label for="googleMapsApiKey">{{ _('Google Maps API key') }}</label>
                  <input type="text" class="form-control" :placeholder="_('Enter your Google Maps API key')" v-model="settings.googlemapsapikey" />
                </div>
                <p><a href="https://developers.google.com/maps/documentation/javascript/get-api-key" target="_blank" class="btn btn-primary"><span class="fa fa-fw fa-google"></span> {{ _('Get key') }}</a></p>
              </div>
              <div class="col-lg-6 leftcol">
                <label></label>
                <p>
                  {{ _('Using Google Maps requires a Google Maps API key attached to the storefront domain.') }}
                  <a href="http://support.johku.com" target="_blank">{{ _('Instructions for getting the key can be found on our support site.') }}</a>
                </p>
              </div>
            </div>
            <div class="row">
              <div class="col-xs-12">
                <h3>{{ _('Storefront type') }}</h3>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-6 leftcol">
                <div class="form-group">
                  <label for="storefrontType">{{ _('Storefront type') }}</label>
                  <select class="form-control" id="storefrontType" v-model="settings.storefronttype" :placeholder="_('Select...')">
                    <option :value="null">{{ _('Default') }}</option>
                    <option value="limited">{{ _('Checkout page for an external site') }}</option>
                  </select>
                </div>
                <div class="form-group" v-if="settings.storefronttype === 'limited'">
                  <label for="externalUrl">{{ _('External site URL') }}</label>
                  <input type="text" class="form-control" :placeholder="_('Enter the URL of the external site')" v-model="settings.externalurl" />
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('layout')" class="tab-pane active" id="layout" v-if="isTabActive('layout')" key="layout">
            <div class="row">
              <div class="col-lg-6 col-lg-push-6 rightcol" data-search="logo">
                <h3>{{ _('Logo') }}</h3>
                <div class="row">
                  <div class="col-sm-6" data-search="profile-picture">
                    <label>{{ _('Profile picture') }}</label>
                    <image-slot :file-id="settings.profile_id" :ext="settings.profile_ext" :owner-key="uploadOwnerKey" upload-slot="profile" input-id="profileImageUpload" image-element-id="companyImage" remove-button-id="removeImageButton" v-on:upload="queueUploads($event, 'profile')" v-on:library="chooseFromLibrary('profile')" v-on:remove="removeImage('profile')" />
                    <hr />
                    <p>{{ _('The Storefront profile picture is used in certain payment methods, the main menu and as the browser\'s favorite icon. If the image is not a square, it will be cropped centrally.') }}</p>
                  </div>
                  <div class="col-sm-6" data-search="storefront-logo">
                    <label>{{ _('Storefront logo') }}</label>
                    <image-slot :file-id="settings.storefrontlogo_id" :ext="settings.storefrontlogo_ext" :owner-key="uploadOwnerKey" upload-slot="storefrontlogo" input-id="storefrontLogoUpload" image-element-id="storefrontLogo" remove-button-id="removeStorefrontLogoButton" v-on:upload="queueUploads($event, 'storefrontlogo')" v-on:library="chooseFromLibrary('storefrontlogo')" v-on:remove="removeImage('storefrontlogo')" />
                    <hr />
                    <p>{{ _('Optional. The logo is shown in the top bar of the storefront in place of the shop name, so it should ideally be a horizontal logo with clearly visible text.') }}</p>
                    <p>{{ _('Recommended size: 240 x 50 px. For a sharp image on mobile and high-resolution screens, upload it at 480 x 100 px (@2x).') }}</p>
                  </div>
                </div>
              </div>
              <div class="col-lg-6 col-lg-pull-6 leftcol">
                <h3>{{ _('Top bar and main menu') }}</h3>
                <div class="form-group">
                  <label for="topBarStyle">{{ _('Top bar style') }}</label>
                  <select class="form-control" id="topBarStyle" :placeholder="_('Select')" v-model="settings.topbarstyle">
                    <option value="default">{{ _('Default') }}</option>
                    <option value="transparent">{{ _('Transparent') }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <div class="checkbox">
                    <label>
                    <input type="checkbox" v-model="settings.hidetopbarlogo" true-value="1" false-value="0" />
                    {{ _('Don\'t display the logo in the top bar') }}
                    </label>
                  </div>
                  <div class="checkbox">
                    <label>
                    <input type="checkbox" v-model="settings.hideshoptitle" true-value="1" false-value="0" />
                    {{ _('Don\'t display the shop title in the top bar') }}
                    </label>
                  </div>
                </div>
                <div data-search="main-menu">
                  <div class="checkbox">
                    <label>
                      <input type="checkbox" v-model="showLoginLink" true-value="1" false-value="0" />
                      {{ _('Show login link in the main menu') }}
                    </label>
                  </div>
                  <div class="checkbox">
                    <label>
                    <input type="checkbox" v-model="settings.promotemerchantregistration" id="promotemerchantregistration" true-value="1" false-value="0" />
                    {{ _('Show merchant registration link in the main menu') }}
                    </label>
                  </div>
                </div>
                <hr />
                <h3>{{ _('Pages and product lists') }}</h3>
                <div class="form-group">
                  <label for="pageTitleLocation">{{ _('Page title location') }}</label>
                  <select class="form-control" id="pageTitleLocation" :placeholder="_('Select location')" v-model="settings.pagetitlelocation">
                    <option value="0">{{ _('Laid over the main image') }}</option>
                    <option value="1">{{ _('Below the main image') }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <label for="productListColumnCount">{{ _('Product list column count') }}</label>
                  <select class="form-control" id="productListColumnCount" v-model="settings.productlistcolumncount">
                    <option :value="null">{{ _('Default') }}</option>
                    <option :value="2">2</option>
                    <option :value="3">3</option>
                    <option :value="4">4</option>
                  </select>
                </div>
                <hr />
                <h3>{{ _('Footer') }}</h3>
                <div class="form-group languageversioned" data-search="footer-description">
                  <label for="storedescription">{{ _('Short description of the store') }}</label>
                  <editor v-model="settings.shopdescription" />
                </div>
                <div data-search="footer-links">
                  <h3>{{ _('Footer links') }}</h3>
                  <draggable v-model="footerLinks" tag="ul" draggable=".list-group-item" :delay="100" :delayOnTouchOnly="true" :componentData="{ attrs: { class: 'o-sortable-groups list-group' }}" @end="persistFooterLinks">
                    <li
                      :class="'list-group-item' + (selectedFooterLinkIndex === linkIndex ? ' list-group-item-info' : '') + (link.visible === false ? ' text-muted' : '')"
                      v-for="(link, linkIndex) in footerLinks"
                      :key="'fl-' + linkIndex + '-' + (link.type === 'product' ? link.product_id : link.key)"
                      v-on:click="footerLinkClicked(linkIndex)"
                    >
                      <span class="fa fa-fw fa-bars"></span>
                      {{ footerLinkLabel(link) }}
                      <span class="label label-default" v-if="link.visible === false">{{ _('Hidden') }}</span>
                    </li>
                  </draggable>
                  <div class="form-group" style="margin-top:10px">
                    <button type="button" class="btn btn-success btn-sm" v-on:click="addFooterProductLinkDialog"><span class="fa fa-fw fa-plus"></span> {{ _('Add product') }}</button>
                    <button type="button" class="btn btn-info btn-sm" v-if="selectedFooterLinkIndex !== null" v-on:click="editFooterLinkDialog"><span class="fa fa-fw fa-pencil"></span> {{ _('Edit') }}</button>
                    <button type="button" class="btn btn-danger btn-sm" v-if="selectedFooterLinkIndex !== null && footerLinks[selectedFooterLinkIndex] && footerLinks[selectedFooterLinkIndex].type === 'product'" v-on:click="removeFooterLink"><span class="fa fa-fw fa-trash"></span> {{ _('Delete') }}</button>
                  </div>
                  <p>{{ _('Customize the order and labels of the legal and product links shown in the store footer.') }}</p>
                  <p>{{ _('Hidden links are not shown in the storefront. Cookies and accessibility still require the matching features or content.') }}</p>
                </div>
                <div data-search="read-more">
                  <h3>{{ _('Read more') }}</h3>
                  <div class="form-group languageversioned">
                    <label for="footertargetid">{{ _('Select a product or enter a web address') }}</label>
                    <ul class="list-group" v-if="parseInt(settings.footertargetid)">
                      <li class="list-group-item">{{ footerTargetName }}<a class="pull-right" href="javascript:;" v-on:click="removeFooterTargetId"><i class="fa fa-fw fa-times"></i></a></li>
                    </ul>
                    <p v-else></p>
                    <button type="button" class="btn btn-success" v-on:click="selectFooterTargetProductDialog"><span class="fa fa-fw fa-plus"></span> {{ _('Select a product') }}</button>
                  </div>
                  <div class="form-group languageversioned">
                    <input type="text" class="form-control" :disabled="!!parseInt(settings.footertargetid)" id="footertargeturl" :placeholder="_('www.website.com')" v-model="settings.footertargeturl" />
                  </div>
                </div>
                <hr />
                <div class="checkbox">
                  <label>
                  <input type="checkbox" v-model="settings.showfooterlogo" id="showfooterlogo" true-value="1" false-value="0" />
                  {{ _('Show company logo in footer') }}
                  </label>
                </div>
                <div class="checkbox">
                  <label>
                  <input type="checkbox" v-model="settings.hidecontacts" id="hidecontacts" true-value="1" false-value="0" />
                  {{ _('Hide contact information from footer') }}
                  </label>
                </div>
                <div class="form-group">
                  <label for="footernavigation">{{ _('Show navigation in footer') }}</label>
                  <select class="form-control" id="footernavigation" v-model="footerNavigation">
                    <option value="categories">{{ _('Show categories') }}</option>
                    <option value="categoriesandcontent">{{ _('Show categories and their content') }}</option>
                    <option value="hide">{{ _('Hide navigation') }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <label for="footernavigationsort">{{ _('Navigation sort') }}</label>
                  <select class="form-control" id="footernavigationsort" v-model="footerNavigationSort">
                    <option value="default">{{ _('Default') }}</option>
                    <option value="alphabetical">{{ _('Alphabetical') }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <label for="footernavigationcolumns">{{ _('Navigation column count') }}</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    class="form-control"
                    id="footernavigationcolumns"
                    :placeholder="_('Empty uses the color scheme default')"
                    :value="footerNavigationColumns"
                    v-on:input="onFooterNavigationColumnsInput"
                  />
                  <p class="help-block">{{ _('Empty uses the color scheme default') }}</p>
                </div>
                <div class="form-group">
                  <label for="footernavigationposition">{{ _('Footer navigation position') }}</label>
                  <select class="form-control" id="footernavigationposition" v-model="footerNavigationPosition">
                    <option value="default">{{ _('Default') }}</option>
                    <option value="right">{{ _('Right column') }}</option>
                    <option value="ownrow">{{ _('Own row') }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('theme')" class="tab-pane active" id="theme" v-if="isTabActive('theme')" key="theme">
            <h3>{{ _('Theme settings') }}</h3>
            <div class="row">
              <div class="col-xs-12">
                <div class="row">
                  <div :class="useVariableColorScheme ? 'col-lg-5' : 'col-xs-12'">
                    <h4>{{ _('Storefront color scheme') }}</h4>
                    <select class="form-control" id="frontpageSearchPos" v-model="useVariableColorScheme" :placeholder="_('Select...')">
                      <option value="0">{{ _('Use a predefined color scheme') }}</option>
                      <option value="1">{{ _('Use customised settings') }}</option>
                    </select>
                    <hr>
                    <template v-if="useVariableColorScheme">
                      <div class="form-group">
                        <label>{{ _('CSS editor') }}</label>
                        <select class="form-control" :value="cssEditorMode" @change="onCssEditorModeChange">
                          <option value="quick">{{ _('Color selection') }}</option>
                          <option value="fields">{{ _('Variable fields') }}</option>
                          <option v-if="isFeatureEnabled('gtmconsent')" value="advanced">{{ _('Advanced CSS') }}</option>
                        </select>
                      </div>
                      <div class="theme-editor-wrap" v-if="quickColorsMode && quickColors">
                        <h4>{{ _('Color selection') }}</h4>
                        <div v-for="field in quickColorFields" :key="field.key" class="group">
                          <label :for="'quick-' + field.key">{{ _(field.label) }}</label>
                          <div class="theme-editor-input">
                            <input class="color" type="color" :value="quickColors[field.key] || quickColorDefault(field.key)" :disabled="!field.required && !quickColors[field.key]" @input="setQuickColor(field.key, $event.target.value)">
                            <input type="text" class="form-control" :id="'quick-' + field.key" :value="quickColors[field.key] || ''" :placeholder="quickColorDefault(field.key)" :disabled="!field.required && !quickColors[field.key]" @change="setQuickColor(field.key, $event.target.value, $event)">
                          </div>
                          <div v-if="!field.required" class="checkbox">
                            <label>
                              <input type="checkbox" :checked="!quickColors[field.key]" @change="setQuickColorUseDefault(field.key, $event.target.checked)">
                              {{ _('Use default') }} <span class="text-muted">({{ _(field.defaultLabel) }})</span>
                            </label>
                          </div>
                        </div>
                        <div v-if="failingContrastChecks.length" class="alert alert-warning contrast-issues-alert" role="alert">
                          <div
                            class="contrast-issues-bar"
                            role="button"
                            tabindex="0"
                            :aria-expanded="contrastIssuesExpanded ? 'true' : 'false'"
                            @click="contrastIssuesExpanded = !contrastIssuesExpanded"
                            @keydown.enter.prevent="contrastIssuesExpanded = !contrastIssuesExpanded"
                            @keydown.space.prevent="contrastIssuesExpanded = !contrastIssuesExpanded"
                          >
                            <span class="contrast-issues-bar-title">
                              <i class="fa fa-fw fa-triangle-exclamation" aria-hidden="true"></i>
                              {{ _('Some colors are hard to read') }} ({{ failingContrastChecks.length }})
                            </span>
                            <span class="contrast-issues-toggle">{{ contrastIssuesExpanded ? _('Hide') : _('Show') }}</span>
                          </div>
                          <div v-show="contrastIssuesExpanded" class="contrast-issues-body">
                            <p>{{ _('Text in these places may be hard to read. Adjust the colors and check the preview.') }}</p>
                            <dl class="contrast-issues-list">
                              <div v-for="check in failingContrastChecks" :key="check.id" class="contrast-issue">
                                <dt>{{ _(check.label) }}</dt>
                                <dd>{{ contrastIssueText(check) }}</dd>
                              </div>
                            </dl>
                          </div>
                        </div>
                        <div v-for="key in quickFontKeys" :key="key" class="group" v-show="cssData && cssData[key]">
                          <label :for="'quick' + key">{{ formatLabel(key) }}</label>
                          <FontSelect
                            v-if="cssData && cssData[key]"
                            v-model="cssData[key].value"
                            :id="'quick' + key"
                            :fonts="availableFonts"
                            :fallback-font="cssFontFallback"
                          />
                        </div>
                        <hr>
                      </div>
                      <div class="theme-editor-wrap" v-if="hasCustomizedSettings && !showAdvancedCssEditor && !quickColorsMode">
                        <h4>{{ _('Fonts and colors') }}</h4>
                        <div v-for="(item, key) in filteredCssData" :key="key" class="group">
                          <label :for="key" class="block text-sm font-medium text-gray-700 capitalize mb-1">{{ formatLabel(key) }}</label>
                          <div class="theme-editor-input">
                            <!-- Font Select Input -->
                            <FontSelect
                              v-if="isFont(key)"
                              v-model="item.value"
                              :id="key"
                              :fonts="availableFonts"
                              :fallback-font="cssFontFallback"
                            />
                            <!-- Font Transform Select Input -->
                            <select v-else-if="isFontTransform(key)"  v-model="item.value" :id="key" class="form-control">
                              <option v-for="option in transformOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                            </select>
                            <!-- Color/Text Input -->
                            <template v-else>
                              <input v-if="isColor(item.value)" class="color" type="color" v-model="item.value" :title="`Select color for ${key}`">
                              <input type="text" v-model.lazy="item.value" :id="key" class="form-control">
                            </template>
                            <!-- Reset Button -->
                            <button @click="resetVariable(key)" class="btn btn-default" :title="_('Reset to default')">
                              <i class="fa fa-fw fa-clock-rotate-left"></i>
                            </button>
                          </div>
                        </div>
                        <hr>
                        <div class="checkbox">
                          <label for="showAll">
                            <input type="checkbox" v-model="showAllVariables" id="showAll" class="">
                            {{ _('Show all variables') }}
                          </label>
                        </div>
                        <hr>
                      </div>
                      <div class="theme-editor-wrap" v-if="hasCustomizedSettings && showAdvancedCssEditor">
                        <h4>{{ _('Advanced CSS') }}</h4>
                        <p class="text-muted">{{ _('Edit storefront CSS freely. Wrap blocks that need consent with /* johku-consent: marketing */ … /* /johku-consent */. Categories: marketing, statistics, preferences (comma-separated; all must be granted).') }}</p>
                        <textarea
                          v-model="rawCss"
                          class="form-control advanced-css-editor"
                          rows="24"
                          spellcheck="false"
                        ></textarea>
                        <hr>
                      </div>
                      <div>
                        <label v-if="hasCustomizedSettings">{{ _('Reset to a base style') }}</label>
                        <label v-else>{{ _('Select a base style') }}</label>
                        <div class="input-group">
                          <select class="form-control" id="defaultCss" v-model="defaultVariables" :placeholder="_('Select...')">
                            <option value="default">{{ translateLabel('Light') }}</option>
                            <option value="dark">{{ translateLabel('Dark') }}</option>
                          </select>
                          <br>
                          <span class="input-group-btn">
                            <button class="btn btn-primary" @click="loadSelectedDefaultCss">{{ _('Apply') }}</button>
                          </span>
                        </div>
                      </div>
                    </template>
                    <template v-else>
                      <div class="theme-selector row" v-if="settings.shopcolorscheme != 'variables'">
                        <div class="col-xs-4 col-lg-3">
                          <a :class="themeClass(1)" v-on:click="setTheme(1)">
                            <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                            <span class="theme-color-1" style="background-color: #FFA726"></span>
                            <span class="theme-color-2" style="background-color: #3C8BD9"></span>
                            <span class="theme-color-3" style="background-color: #455A64"></span>
                            <span class="theme-color-4" style="background-color: #679F38"></span>
                          </a>
                          {{ _('Color scheme') }} 1
                        </div>
                        <div class="col-xs-4 col-lg-3">
                          <a :class="themeClass(2)" v-on:click="setTheme(2)">
                            <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                            <span class="theme-color-1" style="background-color: #CE93D8"></span>
                            <span class="theme-color-2" style="background-color: #00B0FF"></span>
                            <span class="theme-color-3" style="background-color: #455A64"></span>
                            <span class="theme-color-4" style="background-color: #FB8C00"></span>
                          </a>
                          {{ _('Color scheme') }} 2
                        </div>
                        <div class="col-xs-4 col-lg-3">
                          <a :class="themeClass(3)" v-on:click="setTheme(3)">
                            <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                            <span class="theme-color-1" style="background-color: #42A5F5"></span>
                            <span class="theme-color-2" style="background-color: #000000"></span>
                            <span class="theme-color-3" style="background-color: #013E70"></span>
                            <span class="theme-color-4" style="background-color: #01579B"></span>
                          </a>
                          {{ _('Color scheme') }} 3
                        </div>
                        <div class="col-xs-4 col-lg-3">
                          <a :class="themeClass(4)" v-on:click="setTheme(4)">
                            <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                            <span class="theme-color-1" style="background-color: #90A4AE"></span>
                            <span class="theme-color-2" style="background-color: #455A64"></span>
                            <span class="theme-color-3" style="background-color: #000000"></span>
                            <span class="theme-color-4" style="background-color: #388E3C"></span>
                          </a>
                          {{ _('Color scheme') }} 4
                        </div>
                        <div class="col-xs-4 col-lg-3">
                          <a :class="themeClass(5)" v-on:click="setTheme(5)">
                            <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                            <span class="theme-color-1" style="background-color: #71A72D"></span>
                            <span class="theme-color-2" style="background-color: #3C8BD9"></span>
                            <span class="theme-color-3" style="background-color: #403B25"></span>
                            <span class="theme-color-4" style="background-color: #71A72D"></span>
                          </a>
                          {{ _('Color scheme') }} 5
                        </div>
                        <div class="col-xs-4 col-lg-3">
                          <a :class="themeClass(6)" v-on:click="setTheme(6)">
                            <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                            <span class="theme-color-1" style="background-color: #84675C"></span>
                            <span class="theme-color-2" style="background-color: #B0864C"></span>
                            <span class="theme-color-3" style="background-color: #4A4339"></span>
                            <span class="theme-color-4" style="background-color: #607D2E"></span>
                          </a>
                          {{ _('Color scheme') }} 6
                        </div>
                        <div class="col-xs-4 col-lg-3">
                          <a :class="themeClass(7)" v-on:click="setTheme(7)">
                            <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                            <span class="theme-color-1" style="background-color: #AD0520"></span>
                            <span class="theme-color-2" style="background-color: #AD0520"></span>
                            <span class="theme-color-3" style="background-color: #AD0520"></span>
                            <span class="theme-color-4" style="background-color: #333333"></span>
                          </a>
                          {{ _('Color scheme') }} 7
                        </div>
                        <div class="col-xs-4 col-lg-3">
                          <a :class="themeClass(8)" v-on:click="setTheme(8)">
                            <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                            <span class="theme-color-1" style="background-color: #505050"></span>
                            <span class="theme-color-2" style="background-color: #333333"></span>
                            <span class="theme-color-3" style="background-color: #8D8D8D"></span>
                            <span class="theme-color-4" style="background-color: #505050"></span>
                          </a>
                          {{ _('Color scheme') }} 8
                        </div>
                        <div class="col-xs-4 col-lg-3">
                          <a :class="themeClass(9)" v-on:click="setTheme(9)">
                            <span class="theme-color-main" style="background-color: #000000"></span>
                            <span class="theme-color-1" style="background-color: #fdd835"></span>
                            <span class="theme-color-2" style="background-color: #e64a19"></span>
                            <span class="theme-color-3" style="background-color: #000000"></span>
                            <span class="theme-color-4" style="background-color: #999999"></span>
                          </a>
                          {{ _('Color scheme') }} 9
                        </div>
                        <div class="col-xs-4 col-lg-3">
                          <a :class="themeClass(10)" v-on:click="setTheme(10)">
                            <span class="theme-color-main" style="background-color: #000000"></span>
                            <span class="theme-color-1" style="background-color: #81d4fa"></span>
                            <span class="theme-color-2" style="background-color: #0091ea"></span>
                            <span class="theme-color-3" style="background-color: #000000"></span>
                            <span class="theme-color-4" style="background-color: #999999"></span>
                          </a>
                          {{ _('Color scheme') }} 10
                        </div>
                        <div class="col-xs-4 col-lg-3">
                          <a :class="themeClass(11)" v-on:click="setTheme(11)">
                            <span class="theme-color-main" style="background-color: #000000"></span>
                            <span class="theme-color-1" style="background-color: #90a4ae"></span>
                            <span class="theme-color-2" style="background-color: #37474f"></span>
                            <span class="theme-color-3" style="background-color: #000000"></span>
                            <span class="theme-color-4" style="background-color: #b0bec5"></span>
                          </a>
                          {{ _('Color scheme') }} 11
                        </div>
                        <div class="col-xs-4 col-lg-3">
                          <a :class="themeClass(12)" v-on:click="setTheme(12)">
                            <span class="theme-color-main" style="background-color: #000000"></span>
                            <span class="theme-color-1" style="background-color: #4caf50"></span>
                            <span class="theme-color-2" style="background-color: #388e3c"></span>
                            <span class="theme-color-3" style="background-color: #000000"></span>
                            <span class="theme-color-4" style="background-color: #90a4ae"></span>
                          </a>
                          {{ _('Color scheme') }} 12
                        </div>
                        <div class="col-xs-4 col-lg-3">
                          <a :class="themeClass(13)" v-on:click="setTheme(13)">
                            <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                            <span class="theme-color-1" style="background-color: #3f51b5"></span>
                            <span class="theme-color-2" style="background-color: #7986cb"></span>
                            <span class="theme-color-3" style="background-color: #EEEEEE"></span>
                            <span class="theme-color-4" style="background-color: #999999"></span>
                          </a>
                          {{ _('Color scheme') }} 13
                        </div>
                        <div class="col-xs-4 col-lg-3">
                          <a :class="themeClass(14)" v-on:click="setTheme(14)">
                            <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                            <span class="theme-color-1" style="background-color: #558b2f"></span>
                            <span class="theme-color-2" style="background-color: #7cb342"></span>
                            <span class="theme-color-3" style="background-color: #EEEEEE"></span>
                            <span class="theme-color-4" style="background-color: #999999"></span>
                          </a>
                          {{ _('Color scheme') }} 14
                        </div>
                        <div class="col-xs-4 col-lg-3">
                          <a :class="themeClass('14A')" v-on:click="setTheme('14A')">
                            <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                            <span class="theme-color-1" style="background-color: #47662F"></span>
                            <span class="theme-color-2" style="background-color: #47662F"></span>
                            <span class="theme-color-3" style="background-color: #EEEEEE"></span>
                            <span class="theme-color-4" style="background-color: #999999"></span>
                          </a>
                          {{ _('Color scheme') }} 14 (WCAG 2.1)
                        </div>
                        <div class="col-xs-4 col-lg-3">
                          <a :class="themeClass(15)" v-on:click="setTheme(15)">
                            <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                            <span class="theme-color-1" style="background-color: #795548"></span>
                            <span class="theme-color-2" style="background-color: #6d4c41"></span>
                            <span class="theme-color-3" style="background-color: #EEEEEE"></span>
                            <span class="theme-color-4" style="background-color: #999999"></span>
                          </a>
                          {{ _('Color scheme') }} 15
                        </div>
                        <template v-if="shopId === 'tervarumpu' || settings.shopcolorscheme === 'tervarumpu'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('tervarumpu')" v-on:click="setTheme('tervarumpu')">
                              <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                              <span class="theme-color-1" style="background-color: #795548"></span>
                              <span class="theme-color-2" style="background-color: #6d4c41"></span>
                              <span class="theme-color-3" style="background-color: #EEEEEE"></span>
                              <span class="theme-color-4" style="background-color: #999999"></span>
                            </a>
                            Tervarumpu
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'tentsilecamps' || settings.shopcolorscheme === 'tentsile'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('tentsile')" v-on:click="setTheme('tentsile')">
                              <span class="theme-color-main" style="background-color: #e9f0e3"></span>
                              <span class="theme-color-1" style="background-color: #53713d"></span>
                              <span class="theme-color-2" style="background-color: #53713d"></span>
                              <span class="theme-color-3" style="background-color: #c0d725"></span>
                              <span class="theme-color-4" style="background-color: #111"></span>
                            </a>
                            Tentsile Camps
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId.indexOf('seapoint') > -1 || shopId.indexOf('saaristoyhtiot') > -1 || settings.shopcolorscheme === 'seapoint'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('seapoint')" v-on:click="setTheme('seapoint')">
                              <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                              <span class="theme-color-1" style="background-color: #0099C1"></span>
                              <span class="theme-color-2" style="background-color: #0099C1"></span>
                              <span class="theme-color-3" style="background-color: #0099C1"></span>
                              <span class="theme-color-4" style="background-color: #111"></span>
                            </a>
                            Seapoint
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'visitpori' || settings.shopcolorscheme === 'visitpori'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('visitpori')" v-on:click="setTheme('visitpori')">
                              <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                              <span class="theme-color-1" style="background-color: #ec1c24"></span>
                              <span class="theme-color-2" style="background-color: #ec1c24"></span>
                              <span class="theme-color-3" style="background-color: #bcbcbc"></span>
                              <span class="theme-color-4" style="background-color: #1e1e1e"></span>
                            </a>
                            Visit Pori
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'korjaamo' || shopId === 'korjaamokino' || settings.shopcolorscheme === 'korjaamo'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('korjaamo')" v-on:click="setTheme('korjaamo')">
                              <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                              <span class="theme-color-1" style="background-color: #b88741"></span>
                              <span class="theme-color-2" style="background-color: #C8A064"></span>
                              <span class="theme-color-3" style="background-color: #C8A064"></span>
                              <span class="theme-color-4" style="background-color: #455A64"></span>
                            </a>
                            Korjaamo
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'aavalines' || settings.shopcolorscheme === 'aavalines'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('aavalines')" v-on:click="setTheme('aavalines')">
                              <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                              <span class="theme-color-1" style="background-color: #42B4E6"></span>
                              <span class="theme-color-2" style="background-color: #ec1c24"></span>
                              <span class="theme-color-3" style="background-color: #11192b"></span>
                              <span class="theme-color-4" style="background-color: #0C0F1E"></span>
                            </a>
                            Aava Lines
                            </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'kinotar' || settings.shopcolorscheme === 'kinotar'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('kinotar')" v-on:click="setTheme('kinotar')">
                              <span class="theme-color-main" style="background-color: #000000"></span>
                              <span class="theme-color-1" style="background-color: #DD2B63"></span>
                              <span class="theme-color-2" style="background-color: #FFFFFF"></span>
                              <span class="theme-color-3" style="background-color: #CCCCCC"></span>
                              <span class="theme-color-4" style="background-color: #303030"></span>
                            </a>
                            Jämsän Kinotar
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'biomarilyn' || settings.shopcolorscheme === 'biomarilyn'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('biomarilyn')" v-on:click="setTheme('biomarilyn')">
                              <span class="theme-color-main" style="background-color: #000000"></span>
                              <span class="theme-color-1" style="background-color: #cf1218"></span>
                              <span class="theme-color-2" style="background-color: #cf1218"></span>
                              <span class="theme-color-3" style="background-color: #FFFFFF"></span>
                              <span class="theme-color-4" style="background-color: #000000"></span>
                            </a>
                            Bio Marilyn 
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'kannukino' || shopId === 'kannusali' || settings.shopcolorscheme === 'kannukino' || settings.shopcolorscheme === 'kannukinodark'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('kannukino')" v-on:click="setTheme('kannukino')">
                              <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                              <span class="theme-color-1" style="background-color: #f29417"></span>
                              <span class="theme-color-2" style="background-color: #e55912"></span>
                              <span class="theme-color-3" style="background-color: #acc5ce"></span>
                              <span class="theme-color-4" style="background-color: #3b0d33"></span>
                            </a>
                            KannuKino (vaalea) 
                          </div>
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('kannukinodark')" v-on:click="setTheme('kannukinodark')">
                              <span class="theme-color-main" style="background-color:#3b0d33"></span>
                              <span class="theme-color-1" style="background-color: #f29417"></span>
                              <span class="theme-color-2" style="background-color: #e55912"></span>
                              <span class="theme-color-3" style="background-color: #acc5ce"></span>
                              <span class="theme-color-4" style="background-color: #3b0d33"></span>
                            </a>
                            KannuKino (tumma)
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'aurorahut' || settings.shopcolorscheme === 'aurorahut'">
                        <div class="col-xs-4 col-lg-3">
                          <a :class="themeClass('aurorahut')" v-on:click="setTheme('aurorahut')">
                            <span class="theme-color-main" style="background-color:#e8f9f7"></span>
                            <span class="theme-color-1" style="background-color:#00966C"></span>
                            <span class="theme-color-2" style="background-color:#514689"></span>
                            <span class="theme-color-3" style="background-color:#071D49"></span>
                            <span class="theme-color-4" style="background-color:#C67D30"></span>
                          </a>
                          AuroraHut
                        </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'tervademo' || shopId === 'myhelsinki' || settings.shopcolorscheme === 'myhelsinki'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('myhelsinki')" v-on:click="setTheme('myhelsinki')">
                              <span class="theme-color-main" style="background-color:#FFFFFF"></span>
                              <span class="theme-color-1" style="background-color:#cfe4f5"></span>
                              <span class="theme-color-2" style="background-color:#fdeb6c"></span>
                              <span class="theme-color-3" style="background-color:#009b4a"></span>
                              <span class="theme-color-4" style="background-color:#f49ec3"></span>
                            </a>
                            MyHelsinki
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'tervademo' || shopId === 'finlandiatalo'  || settings.shopcolorscheme === 'finlandiatalo'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('finlandiatalo')" v-on:click="setTheme('finlandiatalo')">
                              <span class="theme-color-main" style="background-color:#FFFFFF"></span>
                              <span class="theme-color-1" style="background-color:#008C95"></span>
                              <span class="theme-color-2" style="background-color:#B1E4E3"></span>
                              <span class="theme-color-3" style="background-color:#D0D0CE"></span>
                              <span class="theme-color-4" style="background-color:#000000"></span>
                            </a>
                            Finlandia-talo
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'tervademo' || shopId === 'valo' || settings.shopcolorscheme === 'nowenevalolinna'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('nowenevalolinna')" v-on:click="setTheme('nowenevalolinna')">
                              <span class="theme-color-main" style="background-color:#FFFFFF"></span>
                              <span class="theme-color-1" style="background-color:#468dc2"></span>
                              <span class="theme-color-2" style="background-color:#3b7db0"></span>
                              <span class="theme-color-3" style="background-color:#ffea9c"></span>
                              <span class="theme-color-4" style="background-color:#70c4fe"></span>
                            </a>
                            Valolinna
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'tervademo' || shopId === 'naturaviva'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('naturaviva')" v-on:click="setTheme('naturaviva')">
                              <span class="theme-color-main" style="background-color:#FFFFFF"></span>
                              <span class="theme-color-1" style="background-color:#468dc2"></span>
                              <span class="theme-color-2" style="background-color:#3b7db0"></span>
                              <span class="theme-color-3" style="background-color:#ffea9c"></span>
                              <span class="theme-color-4" style="background-color:#70c4fe"></span>
                            </a>
                            Natura Viva
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'tervademo' || shopId === 'merrycamping'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('merrycamping')" v-on:click="setTheme('merrycamping')">
                              <span class="theme-color-main" style="background-color:#FFFFFF"></span>
                              <span class="theme-color-1" style="background-color:#ff2d21"></span>
                              <span class="theme-color-2" style="background-color:#cccccc"></span>
                              <span class="theme-color-3" style="background-color:#e6e6e6"></span>
                              <span class="theme-color-4" style="background-color:#000000"></span>
                            </a>
                            Merrycamping
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'tervademo' || shopId === 'suomenlinnashop'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('suomenlinna')" v-on:click="setTheme('suomenlinna')">
                              <span class="theme-color-main" style="background-color:#FFFFFF"></span>
                              <span class="theme-color-1" style="background-color:#1160a6"></span>
                              <span class="theme-color-2" style="background-color:#747974"></span>
                              <span class="theme-color-3" style="background-color:#d8006c"></span>
                              <span class="theme-color-4" style="background-color:#000000"></span>
                            </a>
                            SuomenlinnaShop
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'tervademo' || shopId === 'bookingpoint'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('bookingpoint')" v-on:click="setTheme('bookingpoint')">
                              <span class="theme-color-main" style="background-color:#FFFFFF"></span>
                              <span class="theme-color-1" style="background-color:#FFB703"></span>
                              <span class="theme-color-2" style="background-color:#8ECAE6"></span>
                              <span class="theme-color-3" style="background-color:#1E4F68"></span>
                              <span class="theme-color-4" style="background-color:#FB8500"></span>
                            </a>
                            Bookingpoint
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'tervademo' || shopId === 'amosrex'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('amosrex')" v-on:click="setTheme('amosrex')">
                              <span class="theme-color-main" style="background-color:#FFFFFF"></span>
                              <span class="theme-color-1" style="background-color:#000000"></span>
                              <span class="theme-color-2" style="background-color:#000000"></span>
                              <span class="theme-color-3" style="background-color:#cfcfcf"></span>
                              <span class="theme-color-4" style="background-color:#ff6175"></span>
                            </a>
                            Amos Rex
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'tervademo' || shopId === 'amoshem' || shopId === 'amosandersonshem'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('amoshem')" v-on:click="setTheme('amoshem')">
                              <span class="theme-color-main" style="background-color:#f6f2ed"></span>
                              <span class="theme-color-1" style="background-color:#9E744B"></span>
                              <span class="theme-color-2" style="background-color:#b54c36"></span>
                              <span class="theme-color-3" style="background-color:#6B867C"></span>
                              <span class="theme-color-4" style="background-color:#000000"></span>
                            </a>
                            Amos Andersons Hem
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'tervademo' || shopId === 'pyhanasteli'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('pyhanasteli')" v-on:click="setTheme('pyhanasteli')">
                              <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                              <span class="theme-color-1" style="background-color: #0c86a1"></span>
                              <span class="theme-color-2" style="background-color: #000000"></span>
                              <span class="theme-color-3" style="background-color: #054b6e"></span>
                              <span class="theme-color-4" style="background-color: #d3a33c"></span>
                            </a>
                            Pyhän Asteli Resort
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'tervademo' || shopId === 'suma'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('verlansuma')" v-on:click="setTheme('verlansuma')">
                              <span class="theme-color-main" style="background-color: #EFEEEE"></span>
                              <span class="theme-color-1" style="background-color: #504C4B"></span>
                              <span class="theme-color-2" style="background-color: #504C4B"></span>
                              <span class="theme-color-3" style="background-color: #504C4B"></span>
                              <span class="theme-color-4" style="background-color: #000000"></span>
                            </a>
                            Verlan Suma
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'tervademo' || shopId === 'visitoulu'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('visitoulu')" v-on:click="setTheme('visitoulu')">
                              <span class="theme-color-main" style="background-color: #CCCCCC"></span>
                              <span class="theme-color-1" style="background-color: #888888"></span>
                              <span class="theme-color-2" style="background-color: #777777"></span>
                              <span class="theme-color-3" style="background-color: #333333"></span>
                              <span class="theme-color-4" style="background-color: #000000"></span>
                            </a>
                            Visit Oulu
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'tervademo' || shopId === 'visitpohjola'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('visitpohjola')" v-on:click="setTheme('visitpohjola')">
                              <span class="theme-color-main" style="background-color: #CCCCCC"></span>
                              <span class="theme-color-1" style="background-color: #888888"></span>
                              <span class="theme-color-2" style="background-color: #777777"></span>
                              <span class="theme-color-3" style="background-color: #333333"></span>
                              <span class="theme-color-4" style="background-color: #000000"></span>
                            </a>
                            Visit Pohjola
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'tervademo' || shopId === 'discovermuonio'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('discovermuonio')" v-on:click="setTheme('discovermuonio')">
                              <span class="theme-color-main" style="background-color: #eaf3fc"></span>
                              <span class="theme-color-1" style="background-color: #263c59"></span>
                              <span class="theme-color-2" style="background-color: #002191"></span>
                              <span class="theme-color-3" style="background-color: #cce4e5"></span>
                              <span class="theme-color-4" style="background-color: #0a0000"></span>
                            </a>
                            Discover Muonio
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'tervademo' || shopId === 'rokuageopark'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('rokuageopark')" v-on:click="setTheme('rokuageopark')">
                              <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                              <span class="theme-color-1" style="background-color: #27B3B7"></span>
                              <span class="theme-color-2" style="background-color: #9BE3E5"></span>
                              <span class="theme-color-3" style="background-color: #EFEFEF"></span>
                              <span class="theme-color-4" style="background-color: #191919"></span>
                            </a>
                            Rokua Geopark
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'tervademo' || shopId === 'originallapland'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('originallapland')" v-on:click="setTheme('originallapland')">
                              <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                              <span class="theme-color-1" style="background-color: #000000"></span>
                              <span class="theme-color-2" style="background-color: #002f6d"></span>
                              <span class="theme-color-3" style="background-color: #006f3d"></span>
                              <span class="theme-color-4" style="background-color: #faca30"></span>
                            </a>
                            Original Lapland
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'tervademo' || shopId === 'serlachius'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('serlachius')" v-on:click="setTheme('serlachius')">
                              <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                              <span class="theme-color-1" style="background-color: #585857"></span>
                              <span class="theme-color-2" style="background-color: #bcbcbc"></span>
                              <span class="theme-color-3" style="background-color: #df3008"></span>
                              <span class="theme-color-4" style="background-color: #000000"></span>
                            </a>
                            Serlachius
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'minnantesti' || shopId === 'visitkotkahamina'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('visitkotkahamina')" v-on:click="setTheme('visitkotkahamina')">
                              <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                              <span class="theme-color-1" style="background-color: #005395"></span>
                              <span class="theme-color-2" style="background-color: #8dd3d5"></span>
                              <span class="theme-color-3" style="background-color: #cbeaed"></span>
                              <span class="theme-color-4" style="background-color: #000000"></span>
                            </a>
                            Visit Kotka-Hamina
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'minnantesti' || shopId === 'marjoniemicamping'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('marjoniemicamping')" v-on:click="setTheme('marjoniemicamping')">
                              <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                              <span class="theme-color-1" style="background-color: #3EA1DA"></span>
                              <span class="theme-color-2" style="background-color: #19579D"></span>
                              <span class="theme-color-3" style="background-color: #54B957"></span>
                              <span class="theme-color-4" style="background-color: #000000"></span>
                            </a>
                            Marjoniemi Camping
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'minnantesti' || shopId === 'sydansuomessa'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('sydansuomessa')" v-on:click="setTheme('sydansuomessa')">
                              <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                              <span class="theme-color-1" style="background-color: #3EA1DA"></span>
                              <span class="theme-color-2" style="background-color: #19579D"></span>
                              <span class="theme-color-3" style="background-color: #54B957"></span>
                              <span class="theme-color-4" style="background-color: #000000"></span>
                            </a>
                            Sydänsuomessa
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'minnantesti' || shopId === 'tehdasmuseo'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('verla')" v-on:click="setTheme('verla')">
                              <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                              <span class="theme-color-1" style="background-color: #923A22"></span>
                              <span class="theme-color-2" style="background-color: #923A22"></span>
                              <span class="theme-color-3" style="background-color: #f1efec"></span>
                              <span class="theme-color-4" style="background-color: #222222"></span>
                            </a>
                            Verlan tehdasmuseo
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'minnantesti' || shopId === 'visituusikaupunki'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('visituusikaupunki')" v-on:click="setTheme('visituusikaupunki')">
                              <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                              <span class="theme-color-1" style="background-color: #233A74"></span>
                              <span class="theme-color-2" style="background-color: #E4C87B"></span>
                              <span class="theme-color-3" style="background-color: #A3C626"></span>
                              <span class="theme-color-4" style="background-color: #465359"></span>
                            </a>
                            Visit Uusikaupunki
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'minnantesti' || shopId === 'laguuni'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('laguuni')" v-on:click="setTheme('laguuni')">
                              <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                              <span class="theme-color-1" style="background-color: #2D2A45"></span>
                              <span class="theme-color-2" style="background-color: #C0E3D3"></span>
                              <span class="theme-color-3" style="background-color: #F0C3C3"></span>
                              <span class="theme-color-4" style="background-color: #5F80E8"></span>
                            </a>
                            Laguuni
                          </div>
                        </template>
                        <template v-if="shopId === 'tervarumpu' || shopId === 'minnantesti' || shopId === 'visitsaimaashop'">
                          <div class="col-xs-4 col-lg-3">
                            <a :class="themeClass('visitsaimaa')" v-on:click="setTheme('visitsaimaa')">
                              <span class="theme-color-main" style="background-color: #FFFFFF"></span>
                              <span class="theme-color-1" style="background-color: #203584"></span>
                              <span class="theme-color-2" style="background-color: #DFECF9"></span>
                              <span class="theme-color-3" style="background-color: #005857"></span>
                              <span class="theme-color-4" style="background-color: #000000"></span>
                            </a>
                            Visit Saimaa
                          </div>
                        </template>
                      </div>
                      <div class="scheme-fonts-wrap" v-if="isNumberedColorScheme">
                        <hr>
                        <h4>{{ _('Fonts') }}</h4>
                        <div class="form-group">
                          <label for="storefrontfontmain">{{ _('Default') }}</label>
                          <FontSelect
                            id="storefrontfontmain"
                            v-model="storefrontFontMain"
                            :fonts="availableFonts"
                            :fallback-font="storefrontFontMain"
                          />
                        </div>
                        <div class="form-group">
                          <label for="storefrontfonttitle">{{ _('Title') }}</label>
                          <FontSelect
                            id="storefrontfonttitle"
                            v-model="storefrontFontTitle"
                            :fonts="availableFonts"
                            :allow-empty="true"
                            :empty-label="_('Default')"
                            :fallback-font="storefrontFontMain"
                          />
                        </div>
                        <div class="form-group">
                          <label for="storefrontfontbutton">{{ _('Button') }}</label>
                          <FontSelect
                            id="storefrontfontbutton"
                            v-model="storefrontFontButton"
                            :fonts="availableFonts"
                            :allow-empty="true"
                            :empty-label="_('Default')"
                            :fallback-font="storefrontFontMain"
                          />
                        </div>
                      </div>
                    </template>
                  </div>
                  <div class="col-lg-7 theme-preview-col" v-if="useVariableColorScheme">
                    <h4>{{ _('Preview') }}</h4>
                    <StorefrontColorsFrame
                      v-if="showStorefrontColorsFrame"
                      :origin="storefrontOrigin"
                      :locale="storefrontPreviewLocale"
                      :css="themePreviewCss"
                      height="max(600px, calc(100vh - 200px))"
                      @unavailable="onStorefrontColorsFrameUnavailable"
                    />
                    <template v-else>
                      <p v-if="storefrontColorsFrameUnavailable || !hasJohkuStorefront" class="text-muted small">{{ _('The storefront preview is not available, showing a simplified preview.') }} <button v-if="storefrontColorsFrameUnavailable && hasJohkuStorefront" type="button" class="btn btn-link btn-xs" @click="storefrontColorsFrameUnavailable = false">{{ _('Try again') }}</button></p>
                      <StorefrontColorsPreview :tokens="themePreviewTokens" />
                    </template>
                    <p v-if="cssEditorMode === 'advanced'" class="text-muted small">{{ _('The preview shows the color and font variables. Other Advanced CSS rules are not previewed.') }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('redirects')" class="tab-pane active" id="redirects" v-if="isTabActive('redirects') && canAccessRedirects" key="redirects">
            <RedirectSettings />
          </div>
          <div v-bind="tabPanel('cookiebot')" class="tab-pane active" id="cookiebot" v-if="isTabActive('cookiebot') && isFeatureEnabled('cookiebot')" key="cookie">
            <div class="row">
              <div class="col-xs-12">
                <h3>{{ _('Cookie consent management') }}</h3>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-12">
                <div class="form-group">
                  <div class="radio">
                    <label><input type="radio" id="cookieCS" name="validity" v-model="selectedCookiemanager" value="consentstudio" /> {{ _('Use Consent Studio for cookie consent management') }}</label>
                  </div>
                  <div class="radio">
                    <label><input type="radio" id="cookieBot" name="validity" v-model="selectedCookiemanager" value="cookiebot" /> {{ _('Use Cookiebot for cookie consent management') }}</label>
                  </div>
                  <div class="radio">
                    <label><input type="radio" id="cookieCustom" name="validity" v-model="selectedCookiemanager" value="custom" /> {{ _('Use other cookie consent management service') }}</label>
                  </div>
                  <div class="radio">
                    <label><input type="radio" id="cookieNone" name="validity" v-model="selectedCookiemanager" value="" /> {{ _('Not in use') }}</label>
                  </div>
                </div>
              </div>
            </div>
            <template v-if="selectedCookiemanager == 'cookiebot'">
              <div class="row">
                <div class="col-sm-12">
                  <h3>{{ _('Cookiebot settings') }}</h3>
                  <div class="form-group">
                    <label for="cookiebotid">{{ _('Cookiebot ID') }}</label>
                    <input class="form-control" v-model="settings.cookiebotid" id="cookiebotid" type="text" />
                    <p class="text-info"><i class="fa fa-fw fa-info"></i> {{ _('Either the Cookiebot id or a script tag that contains the Cookiebot id.') }}</p>
                  </div>
                </div>
              </div>
              <!-- -->
              <div class="row">
                <div class="col-xs-12">
                  <p>{{ _('This feature requires a Cookiebot account. You can create one or link an existing account to the Johku Cookiebot service by clicking the button below.') }}</p>
                  <p><a href="https://manage.cookiebot.com/goto/signup?rid=IJD9L" target="_blank" class="btn btn-success" rel="noopener noreferrer">{{ _('Create a new Cookiebot account or link your existing account') }}</a></p>
                </div>
              </div>
            </template>
             <!-- Consent Studio CMP -->
            <template v-if="selectedCookiemanager == 'consentstudio'">
              <div class="row">
                <div class="col-xs-12">
                  <h3>{{ _('Consent Studio settings') }}</h3>
                  <div class="form-group">
                    <label>{{ _('Domain name')}}</label>
                    <input class="form-control" readonly :value="(settings.shopdomain ? settings.shopdomain.replace('www.','') : shopId + '.johku.com')" />
                  </div>
                  <p>{{ _('This feature requires a Consent Studio account. Learn more about the service by clicking the button below.') }}</p>
                  <p><a href="https://consent.studio" target="_blank" class="btn btn-success" rel="noopener noreferrer">{{ _('Consent Studio') }}</a></p>
                </div>
              </div>
            </template>
            <!-- Custom cookie manager -->
            <template v-if="selectedCookiemanager == 'custom'">
              <div class="row">
                <div class="col-xs-12">
                  <h3>{{ _('Other cookie manager service') }}</h3>
                  <div class="checkbox">
                    <label>
                      <input type="checkbox" v-model="settings.customcookiemanager" true-value="1" false-value="0" :disabled="settings.cookiebotid ? 'disabled' : false"/>
                      {{ _('I have implemented cookie management using Google Tag Manager') }}
                    </label>
                  </div>
                  <p class="text-info" v-if="isFeatureEnabled('gtmconsent')"><i class="fa fa-fw fa-info"></i> {{ _('Your cookie management platform in Google Tag Manager must update Google Consent Mode v2, or push a johku_consent_update dataLayer event with johku_consent.marketing, johku_consent.statistics and johku_consent.preferences.') }}</p>
                </div>
              </div>
            </template>
          </div>
        </transition-group>
      </div>
    </div>
    <nav class="save-bar" v-if="isSaveBarVisible">
      <div class="row">
        <div class="col-md-12">
          <div class="pull-right">
            <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
          </div>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import { storefrontOrigin as shopStorefrontOrigin } from '~/lib/storefrontOrigin'
import axios from 'axios'
import { mapGetters } from 'vuex'
import components from '~/lib/components'
import formatters from '~/lib/formatters'
import settingseditors from '~/lib/settingseditors'
import settingshelpers from '~/lib/settingshelpers'
import uploadaware from '~/lib/uploadaware'
import { CategorySelector, ProductSelector, ModalCategoryDetails as CategoryDetails, Datatable, Editor, LinkedCategorySelector, FooterLinkEditor, RedirectSettings } from '#components'
import { addVersionToEnableCatalog } from '~/lib/widgetvisibility'
import { viewLabel } from '~/lib/productListViews'
import { sanitizeSeeds, normalizeHex, deriveStorefrontTokens, contrastChecks, buildQuickColorsCss, seedsFromTokens, baseToPreset, presetToBase } from '~/lib/storefrontColors'

const ADVANCED_CSS_SENTINEL = '/* johku-editor: advanced */'
const ADVANCED_CSS_SENTINEL_RE = /\/\*\s*johku-editor:\s*advanced\s*\*\//i
const CONSENT_BLOCK_RE = /\/\*\s*johku-consent:\s*[^*]+?\s*\*\/[\s\S]*?\/\*\s*\/johku-consent\s*\*\//gi
const DEFAULT_STOREFRONT_FONT = '"Rethink Sans", sans-serif'
const NUMBERED_COLOR_SCHEME_RE = /^\d+A?$/

export default {
  data () {
    const $this = this
    const _ = useNuxtApp().$translate
    return {
      hasCustomizedSettings: false,
      customThemeFirstAccess: false,
      showAllVariables: false,
      advancedCssEditor: false,
      rawCss: '',
      // Set by a base-style reset so the next save does not append the old stored CSS.
      discardStoredStorefrontCss: false,
      defaultVariables: 'default',
      loadedDefaultVariables: null,
      quickColors: null,
      quickColorsMode: false,
      storefrontColorsFrameUnavailable: false,
      contrastIssuesExpanded: false,
      quickColorFields: [
        { key: 'primary', label: 'Primary', required: true },
        { key: 'accent', label: 'Accent', required: true },
        { key: 'background', label: 'Background', required: true },
        { key: 'text', label: 'Text', required: true },
        { key: 'header', label: 'Header', required: false, defaultLabel: 'Same as background' },
        { key: 'footer', label: 'Footer', required: false, defaultLabel: 'Same as text' },
        { key: 'success', label: 'Ok / Success', required: false, defaultLabel: 'Base style default' },
        { key: 'error', label: 'Error', required: false, defaultLabel: 'Base style default' }
      ],
      quickFontKeys: ['--johku-font-main', '--johku-font-title', '--johku-font-button'],
      allTags: [],
      keywordToAdd: '',
      search: '',
      affectedWidgets: [],
      allCategories: [],
      allCategoryProducts: [],
      selectedCategoryProducts: [],
      topLevelSelectedCategoryProducts: [],
      defaultSelectedCategoryProducts: [],
      categoryProducts: {},
      taintedCategories: {},
      allProducts: [],
      nextTopLevel: [],
      activeTab: 'frontpage',
      selectedFrontpageCategory: null,
      selectedFeaturedLink: null,
      featuredCategoryGroups: [],
      footerLinks: [],
      selectedFooterLinkIndex: null,
      lastProductLimit: null,
      productLimitHideSentinel: 4294967295,
      selectedDisplayedCategory: null,
      showOtherProducts: false,
      newSeoTag: '',
      pagesTableOptions: {
        responsiveLayoutCollapseFormatter: $this.collapseFormatter(),
        columns: [
        {
          title: 'ID',
          field: 'id',
          width: 70,
          responsive: 0
        },
        {
          title: _('Page name'),
          field: 'name',
          widthGrow: 3,
          responsive: 0
        },
        {
          title: _('Short description'),
          field: 'description',
          widthGrow: 6,
          formatter: 'html'
        },
        {
          title: _('Priority'),
          field: 'priority',
          widthGrow: 1,
        },
        ],
        initialSort: [{
          column: 'priority',
          dir: 'asc'
        }],
        rowClick (e, row) {
          let id = row.getCell('id').getValue()
          let name = row.getCell('name').getValue()
          $this.editPage(id, name)
        }
      },
      widgetsTableOptions: {
        responsiveLayoutCollapseFormatter: $this.collapseFormatter(),
        columns: [
        {
          title: '',
          width: 50,
          hozAlign: 'center',
          headerSort: false,
          responsive: 0,
          formatter: (cell) => {
            const rowData = cell.getRow().getData()
            const widgetId = parseInt(rowData.id, 10)
            if (!$this.affectedWidgetIds.has(widgetId)) {
              return ''
            }
            const affected = $this.affectedWidgetById[widgetId]
            const title = affected && affected.widgetType === 'Category'
              ? $this._('Selected category is not visible in widgets for the current language')
              : $this._('Selected product is not published for the current language')
            return '<i class="fa fa-exclamation-triangle text-danger" title="' + title + '"></i>'
          }
        },
        {
          title: 'ID',
          field: 'id',
          width: 70,
          responsive: 0
        },
        {
          title: _('Name'),
          field: 'name',
          widthGrow: 3,
          responsive: 0
        },
        {
          title: _('Identifier'),
          field: 'identifier',
          widthGrow: 6,
          formatter: 'html'
        },
        {
          title: _('Type'),
          field: 'type',
          widthGrow: 3,
          formatter: this.widgetTypeFormatter()
        },
        ],
        initialSort: [{
          column: 'name',
          dir: 'asc'
        }],
        rowClick (e, row) {
          let id = row.getCell('id').getValue()
          let name = row.getCell('name').getValue()
          $this.editWidget(id, name)
        }
      },
      defaultCssData: null,
      // This will hold the user's customized data.
      cssData: null,
      // Available fonts for the dropdowns
      defaultAvailableFonts: [
        '"Rethink Sans", sans-serif',
        '"Roboto", sans-serif',
        '"Open Sans", sans-serif',
        '"Lato", sans-serif',
        '"Montserrat", sans-serif',
        '"Inter", sans-serif',
        '"Lora", sans-serif',
        '"Merriweather", serif',
        '"Playfair Display", serif',
        '"Oswald", sans-serif',
        '"Rubik", sans-serif',
        'Georgia, serif',
        '"Times New Roman", Times, serif',
        'Arial, Helvetica, sans-serif'
      ],
      copyButtonText: 'Copy',
      showLoginLink: 0
    }
  },
  mixins: [components, formatters, settingseditors, settingshelpers, uploadaware],
  computed: {
    tabList () {
      return this.editorTabs([
        { id: 'frontpage', label: this._('Front page') },
        { id: 'search', label: this._('Search') },
        { id: 'menu', label: this._('Menu structure') },
        { id: 'widgets', label: this._('Widgets') },
        { id: 'metadata', label: this._('Metadata and tracking') },
        { id: 'layout', label: this._('storefront settings|Layout') },
        { id: 'theme', label: this._('Theme') },
        { id: 'cookiebot', label: this._('Cookie management'), hidden: !this.isFeatureEnabled('cookiebot') },
        { id: 'redirects', label: this._('Redirects'), hidden: !this.canAccessRedirects },
        { id: 'settings', label: this._('Other') }
      ])
    },
    isDefaultVersion () {
      return !this.version || this.version === 'default' || this.version === (this.settings && this.settings.defaultversion)
    },
    uploadOwner () {
      // Shared with CompanyProfile, which edits the same settings images
      return { type: 'settings', id: 1, label: this._('Company profile'), open: null }
    },
    ...mapGetters(['userData']),
    cssEditorMode: {
      get () {
        if (this.quickColorsMode) {
          return 'quick'
        }
        return this.showAdvancedCssEditor ? 'advanced' : 'fields'
      },
      set (mode) {
        if (mode === 'quick') {
          this.switchToQuickColors()
        } else if (this.quickColorsMode) {
          this.leaveQuickColors(mode)
        } else if (mode === 'advanced') {
          this.switchToAdvancedCssEditor()
        } else {
          this.switchToFieldsCssEditor()
        }
      }
    },
    showAdvancedCssEditor () {
      return this.advancedCssEditor && this.isFeatureEnabled('gtmconsent')
    },
    // The storefront preview page reads only the first :root block of this CSS.
    themePreviewCss () {
      if (this.cssEditorMode === 'advanced') {
        return this.sanitizeAdvancedCss(this.rawCss || '')
      }
      return buildQuickColorsCss(this.sanitizeCustomCss(this.cssData || {}), null)
    },
    // cssData is not updated while editing Advanced CSS, so that mode reads rawCss.
    themePreviewTokens () {
      const data = this.cssEditorMode === 'advanced'
        ? this.parseCss(this.stripAdvancedCssMeta(this.rawCss || ''))
        : (this.cssData || {})
      const tokens = {}
      for (const key in data) {
        tokens[key] = data[key].value
      }
      return tokens
    },
    storefrontPreviewLocale () {
      const locale = (this.settings && this.settings.defaultversion) || ''
      return /^[a-z]{2}_[A-Z]{2}$/.test(locale) ? locale : 'fi_FI'
    },
    hasJohkuStorefront () {
      return !(this.settings && this.settings.storefronttype === 'limited')
    },
    showStorefrontColorsFrame () {
      return this.hasJohkuStorefront && !this.storefrontColorsFrameUnavailable
    },
    failingContrastChecks () {
      const seeds = sanitizeSeeds(this.quickColors)
      if (!seeds) {
        return []
      }
      return contrastChecks(seeds, deriveStorefrontTokens(seeds, this.quickColorsBaseTokens(), this.quickColorsCurrentTokens())).filter(check => !check.ok)
    },
    availableFonts () {
      if (this.userData && this.userData.email && (this.userData.email.includes('@johku.com') || this.userData.email.includes('@aptual.fi') || this.userData.email.includes('@jalusta.com'))) {
        return ['"Corisande", sans-serif', '"Corisande Light", sans-serif', ...this.defaultAvailableFonts]
      } else {
        return this.defaultAvailableFonts
      }
    },
    storefrontOrigin () {
      return shopStorefrontOrigin(this.shopId, this.settings && this.settings.shopdomain, useRuntimeConfig().public.storefrontDomain)
    },
    cssFontFallback () {
      if (!this.cssData || !this.cssData['--johku-font-main']) {
        return DEFAULT_STOREFRONT_FONT
      }
      return this.cssData['--johku-font-main'].value || DEFAULT_STOREFRONT_FONT
    },
    transformOptions () {
      const opts = [
        { text: this._('Default'), value: 'inherit' },
        { text: this._('Uppercase'), value: 'uppercase' }
      ]
      return opts
    },
    filteredCssData() {
      if (this.showAllVariables || !this.cssData) {
        return this.cssData
      }
      const filtered = {};
      for (const key in this.cssData) {
        if (Object.hasOwnProperty.call(this.cssData, key) && this.cssData[key].isBasic) {
          filtered[key] = this.cssData[key]
        }
      }
      return filtered
    },
    useVariableColorScheme: {
      get () {
        return (this.settings && this.settings.shopcolorscheme === 'variables' ? 1 : 0)
      },
      set (newValue) {
        this.setTheme((parseInt(newValue) ? 'variables' : 1))
        if (parseInt(newValue)) {
          this.openQuickColorsOnFirstAccess()
        }
      }
    },
    isNumberedColorScheme () {
      const scheme = this.settings && this.settings.shopcolorscheme
      return NUMBERED_COLOR_SCHEME_RE.test(String(scheme || ''))
    },
    storefrontFontMain: {
      get () {
        return (this.settings && this.settings.storefrontfontmain) || DEFAULT_STOREFRONT_FONT
      },
      set (value) {
        if (this.settings) {
          this.settings.storefrontfontmain = value || DEFAULT_STOREFRONT_FONT
        }
      }
    },
    storefrontFontTitle: {
      get () {
        return (this.settings && this.settings.storefrontfonttitle) || ''
      },
      set (value) {
        if (this.settings) {
          this.settings.storefrontfonttitle = value || ''
        }
      }
    },
    storefrontFontButton: {
      get () {
        return (this.settings && this.settings.storefrontfontbutton) || ''
      },
      set (value) {
        if (this.settings) {
          this.settings.storefrontfontbutton = value || ''
        }
      }
    },
    allSearchKeywords () {
      return [...new Set(this.allTags.filter(t => t.category === 'product' || t.category === 'seo').map(t => t.title))].sort((a, b) => a > b ? 1 : -1)
    },
    pagesUrl () {
      return this.baseDomain + this.shopId + '/pages.json?apikey=' + this.apiKey + this.versionStr
    },
    widgetsUrl () {
      return this.baseDomain + this.shopId + '/widgets.json?apikey=' + this.apiKey
    },
    affectedWidgetIds () {
      return new Set(this.affectedWidgets.map(w => parseInt(w.widgetId, 10)))
    },
    affectedWidgetById () {
      const map = {}
      for (const widget of this.affectedWidgets) {
        map[parseInt(widget.widgetId, 10)] = widget
      }
      return map
    },
    affectedProductWidgets () {
      return this.affectedWidgets.filter(w => w.widgetType !== 'Category')
    },
    affectedCategoryWidgets () {
      return this.affectedWidgets.filter(w => w.widgetType === 'Category')
    },
    publishableAffectedProducts () {
      const seen = new Set()
      const products = []
      for (const widget of this.affectedWidgets) {
        if (widget.productMissing || !widget.productId || seen.has(widget.productId)) {
          continue
        }
        seen.add(widget.productId)
        products.push(widget)
      }
      return products
    },
    allowableAffectedCategories () {
      const seen = new Set()
      const categories = []
      for (const widget of this.affectedCategoryWidgets) {
        if (widget.categoryMissing || !widget.categoryId || seen.has(widget.categoryId)) {
          continue
        }
        seen.add(widget.categoryId)
        categories.push(widget)
      }
      return categories
    },
    sortedCategories () {
      if (!this.allCategories || !this.allCategories.length) {
        return []
      }
      return [...this.allCategories].sort((a, b) => a.name > b.name ? 1 : -1) // Sort a clone
    },
    selectedCookiemanager: {
      get () {
        if (this.settings.cookiemanager && this.settings.cookiemanager != '') {
          return this.settings.cookiemanager
        }
        if (!parseInt(this.settings.customcookiemanager) && this.settings.cookiebotid) {
          return 'cookiebot'
        }
        if (parseInt(this.settings.customcookiemanager)) {
          return 'custom'
        }
        return ''
      },
      set (newValue) {
        if (newValue != 'custom') {
          this.settings.customcookiemanager = false
        }
        if (newValue != 'cookiebot') {
          this.settings.cookiebotid = ''
        }
        this.settings.cookiemanager = newValue
      }
    },
    settings: {
      get () {
        return this.localSettings ? this.localSettings : this.shopSettings(this.shopId)
      },
      set (newValue) {
        this.localSettings = newValue
      }
    },
    versions: {
      get () {
        if (!this.settings || !this.settings.versions) {
          return []
        }
        return this.settings.versions.split(';')
      },
      set (newValue) {
        // No need for this here
      }
    },
    storefrontversions () {
      if (!this.settings) {
        return []
      }
      return this.settings.storefrontversions ? this.settings.storefrontversions.split(';') : []
    },
    featuredCategories: {
      get () {
        return this.settings && this.settings.featured_categories ? this.settings.featured_categories.split(';') : []
      },
      set (newValue) {
        this.settings.featured_categories = newValue.join(';')
      }
    },
    frontpageCategories: {
      get () {
        return this.featuredCategories.slice(1)
      },
      set (newValue) {
        this.settings.featured_categories = this.featuredCategory + (newValue && newValue.length ? ';' + newValue.join(';') : '')
      }
    },
    featuredCategory: {
      get () {
        return this.featuredCategories.length ? this.featuredCategories[0] : null
      },
      set (newValue) {
        this.settings.featured_categories = newValue + (this.featuredCategories.length > 1 ? ';' + this.featuredCategories.slice(1).join(';') : '')
      }
    },
    productLimitMode: {
      get () {
        const n = parseInt(this.settings && this.settings.productlimit, 10)
        if (this.isHiddenProductLimit(n)) {
          return 'hide'
        }
        if (n > 0) {
          return 'limit'
        }
        return 'none'
      },
      set (mode) {
        const current = parseInt(this.settings.productlimit, 10)
        if (current > 0 && !this.isHiddenProductLimit(current)) {
          this.lastProductLimit = current
        }
        if (mode === 'hide') {
          this.settings.productlimit = this.productLimitHideSentinel
        } else if (mode === 'limit') {
          const n = this.lastProductLimit || 8
          this.settings.productlimit = n
        } else {
          this.settings.productlimit = 0
        }
      }
    },
    productLimitCount: {
      get () {
        const n = parseInt(this.settings && this.settings.productlimit, 10)
        if (n > 0 && !this.isHiddenProductLimit(n)) {
          return n
        }
        return this.lastProductLimit || ''
      },
      set (value) {
        const n = parseInt(value, 10)
        if (n > 0 && !this.isHiddenProductLimit(n)) {
          this.lastProductLimit = n
          this.settings.productlimit = n
        }
      }
    },
    footerNavigation: {
      get () {
        const value = this.settings && this.settings.footernavigation
        if (value === 'categoriesandcontent' || value === 'hide' || value === 'categories') {
          return value
        }
        return 'categories'
      },
      set (value) {
        this.settings.footernavigation = value
      }
    },
    footerNavigationSort: {
      get () {
        const value = this.settings && this.settings.footernavigationsort
        if (value === 'alphabetical') {
          return 'alphabetical'
        }
        return 'default'
      },
      set (value) {
        this.settings.footernavigationsort = value
      }
    },
    footerNavigationColumns () {
      const value = this.settings && this.settings.footernavigationcolumns
      if (value === null || value === undefined || value === '') {
        return ''
      }
      const n = parseInt(value, 10)
      return n >= 1 ? n : ''
    },
    footerNavigationPosition: {
      get () {
        const value = this.settings && this.settings.footernavigationposition
        if (value === 'ownrow' || value === 'right') {
          return value
        }
        return 'default'
      },
      set (value) {
        this.settings.footernavigationposition = value
      }
    },
    slideshowItems: {
      get () {
        let itemList = this.settings && this.settings.slideshow_order ? this.settings.slideshow_order.split(';') : ['*']
        return itemList
      },
      set (newValue) {
        this.settings.slideshow_order = newValue.join(';')
      }
    },
    footerTargetName () {
      if (!parseInt(this.settings.footertargetid)) {
        return ''
      }
      const product = this.allProducts.find(p => parseInt(p.id, 10) === parseInt(this.settings.footertargetid, 10))
      if (product) {
        return product.name
      }
      return ''
    },
    displayedCategories: {
      get () {
        return this.allCategories.filter(c => parseInt(c.enable_catalog) || (c.enable_catalog && c.enable_catalog.match && c.enable_catalog.match(/_/)))
      },
      set (newValue) {
        newValue.forEach(v => {
          if (!v.enable_catalog) {
            v.enable_catalog = this.storefrontversions.join(';')
          }
        })
        this.allCategories = [...newValue, ...this.privateCategories]
      }
    },
    privateCategories: {
      get () {
        return this.allCategories.filter(c => parseInt(c.enable_catalog) === 0 || !c.enable_catalog).sort((a, b) => a.name > b.name ? 1 : -1)
      },
      set (newValue) {
        newValue.forEach(v => {
          if (v.enable_catalog) {
            v.enable_catalog = null
          }
        })
        this.allCategories = [...this.displayedCategories, ...newValue]
      }
    },
    selectedCategoryName () {
      if (!this.selectedDisplayedCategory) {
        return ''
      }
      let category = this.allCategories.find(c => c.id === this.selectedDisplayedCategory)
      if (category) {
        return category.name
      }
      return ''
    },
    prioritySelectedCategoryProducts: {
      get () {
        return this.selectedCategoryProducts.filter(p => parseInt(p.priority) > 0).sort((a, b) => parseInt(a.priority) < parseInt(b.priority) ? 1 : -1)
      }
    },
    lastSelectedCategoryProducts: {
      get () {
        return this.selectedCategoryProducts.filter(p => parseInt(p.priority) < 0).sort((a, b) => parseInt(a.priority) < parseInt(b.priority) ? 1 : -1)
      }
    },
    seoTags: {
      get () {
        return this.settings && this.settings.seotags ? this.settings.seotags.split(';').filter(t => !!t) : []
      },
      set (newValue) {
        this.settings.seotags = newValue.join(';')
      }
    },
    searchKeywords: {
      get () {
        return this.settings && this.settings.searchkeywords ? this.settings.searchkeywords.split(';').filter(t => !!t) : []
      },
      set (newValue) {
        this.settings.searchkeywords = newValue.join(';')
      }
    },
    isSaveBarVisible () {
      // Widgets and redirects save each item on their own
      return this.activeTab !== 'widgets' && this.activeTab !== 'redirects'
    },
    canAccessRedirects () {
      return this.userData && this.userData.userlevel >= 7
    }
  },
  watch: {
    activeTab (newTab) {
      if (newTab === 'widgets') {
        this.loadWidgetVisibility()
      }
    },
    version () {
      if (this.activeTab === 'widgets') {
        this.loadWidgetVisibility()
      }
    },
    // Run before the unsaved-changes watcher. That watcher saves the link list,
    // and on reload the list is still empty, so it would overwrite the loaded names.
    localSettings: {
      flush: 'sync',
      handler () {
        this.ensureFooterLinksInitialized()
      }
    },
    cssData: {
      handler(newData) {
        // This watcher ensures all data changes are sanitized.
        // It's useful for inputs that update immediately (like color pickers) 
        // and for sanitizing text inputs on the 'change' event (via v-model.lazy).
        for (const key in newData) {
          if (newData[key] && typeof newData[key].value === 'string') {
            const sanitizedValue = this.sanitizeCssValue(newData[key].value)
            if (newData[key].value !== sanitizedValue) {
              this.hasCustomizedSettings = true
              this.cssData[key].value = sanitizedValue
            }
          }
        }
      },
      deep: true
    },
    featuredCategory: {
      deep: true,
      async handler (newValue) {
        if (!newValue) {
          this.allCategoryProducts = []
          return
        }
        this.allCategoryProducts = await this.loadCategoryProducts(newValue)
        this.updateSlideshowItems()
      }
    },
    'settings.shopdomain': {
      handler (newValue) {
        if (newValue && newValue.match && newValue.match(/johku\.com$/)) {
          this.settings.shopdomain = ''
          this.showError(this._('Setting a custom *.johku.com domain is not supported.'))
        }
      }
    },
    'settings.searchdomain': {
      handler (newValue) {
        if (newValue && newValue.match && newValue.match(/johku\.com$/)) {
          this.settings.searchdomain = ''
          this.showError(this._('Setting a custom *.johku.com domain is not supported.'))
        }
      }
    },
    'settings.cookiebotid': {
      handler (newValue) {
        if(newValue) {
          this.settings.customcookiemanager = 0
        }
      }
    }
  },
  methods: {
    productListViewLabel (view) {
      return viewLabel(view, this._)
    },
    isHiddenProductLimit (n) {
      const v = parseInt(n, 10)
      return v < 0 || v === this.productLimitHideSentinel
    },
    normalizeLinkProductLimit (value) {
      if (value === undefined || value === null || value === '') {
        return null
      }
      const n = Number(value)
      if (!isFinite(n)) {
        return null
      }
      if (n < 0 || n === this.productLimitHideSentinel) {
        return this.productLimitHideSentinel
      }
      if (n === 0) {
        return 0
      }
      return n
    },
    /**
     * Sanitizes a CSS value to prevent injection and breaking changes.
     * @param {string} value - The CSS value to sanitize.
     * @returns {string} - The sanitized value.
     */
    toCSS(data) {
      if (!data) {
        return ':root {}'
      }
      let cssString = ':root {\n'
      for (const key in data) {
        if (Object.hasOwnProperty.call(data, key)) {
          cssString += `    ${key}: ${data[key].value};\n`
        }
      }
      cssString += '}'
      return cssString
    },
    isAdvancedCss(cssString) {
      return typeof cssString === 'string' && ADVANCED_CSS_SENTINEL_RE.test(cssString)
    },
    ensureAdvancedCssSentinel(cssString) {
      const body = (cssString || '').replace(ADVANCED_CSS_SENTINEL_RE, '').trim()
      return ADVANCED_CSS_SENTINEL + '\n' + body
    },
    stripAdvancedCssMeta(cssString) {
      if (typeof cssString !== 'string') {
        return ''
      }
      return cssString
        .replace(ADVANCED_CSS_SENTINEL_RE, '')
        .replace(CONSENT_BLOCK_RE, '')
        .trim()
    },
    sanitizeAdvancedCss(value) {
      if (typeof value !== 'string') {
        return ''
      }
      // Strip `<` so `</style>` cannot break out of a stylesheet. Keep `>` for
      // child combinators and modern media-query ranges (`width > 600px`).
      let css = value.replace(/</g, '')
      css = css.replace(/url\s*\(\s*['"]?\s*(?:javascript|vbscript)\s*:/gi, 'url(')
      css = css.replace(/@import\s+['"]?\s*(?:javascript|vbscript)\s*:/gi, '@import ')
      css = css.replace(/expression\s*\(/gi, '(')
      css = css.replace(/-moz-binding\s*:/gi, 'moz-binding:')
      css = css.replace(/\bbehavior\s*:/gi, 'beh:')
      css = css.replace(/\bbinding\s*:/gi, 'bind:')
      return css
    },
    mergeParsedCssWithDefaults(parsed) {
      const defaultParsed = this.parseCss(this.defaultCssData)
      const finalData = {}
      for (const key in defaultParsed) {
        if (Object.hasOwnProperty.call(defaultParsed, key)) {
          const isBasic = defaultParsed[key].isBasic
          const value = (parsed && parsed[key]) ? parsed[key].value : defaultParsed[key].value
          finalData[key] = { value, isBasic }
        }
      }
      return finalData
    },
    switchToAdvancedCssEditor() {
      if (!this.isFeatureEnabled('gtmconsent')) {
        return
      }
      if (!this.advancedCssEditor) {
        const base = this.rawCss && this.isAdvancedCss(this.rawCss)
          ? this.rawCss
          : this.toCSS(this.cssData)
        this.rawCss = this.ensureAdvancedCssSentinel(base)
      }
      this.advancedCssEditor = true
      this.hasCustomizedSettings = true
    },
    switchToFieldsCssEditor() {
      if (!this.advancedCssEditor) {
        return
      }
      const stripped = this.stripAdvancedCssMeta(this.rawCss)
      const hasExtraRules = this.cssHasNonVariableRules(stripped)
      const applySwitch = () => {
        this.cssData = this.mergeParsedCssWithDefaults(this.sanitizeCustomCss(this.parseCss(stripped)))
        this.rawCss = ''
        this.advancedCssEditor = false
      }
      if (hasExtraRules) {
        this.showDialog({
          title: this._('Switching to variable fields will remove advanced CSS rules that are not theme variables.'),
          width: 800,
          buttons: [
            {
              title: this._('Cancel'),
              icon: 'ban',
              handler: () => {}
            },
            {
              title: this._('Switch'),
              class: 'warning',
              icon: 'check',
              handler: applySwitch
            }
          ],
        })
        return
      }
      applySwitch()
    },
    onCssEditorModeChange(event) {
      const select = event.target
      this.cssEditorMode = select.value
      // Put the select back if the switch was cancelled or is waiting for a confirmation.
      this.$nextTick(() => {
        select.value = this.cssEditorMode
      })
    },
    onStorefrontColorsFrameUnavailable() {
      this.storefrontColorsFrameUnavailable = true
    },
    contrastIssueText(check) {
      return this._('Contrast %s:1, needs at least %s:1').replace('%s', check.ratio.toFixed(2)).replace('%s', check.min)
    },
    // The stored CSS that quick mode writes over: its Advanced CSS rules are kept after the generated block.
    // A base-style reset drops that stored CSS, so it is not put back on save.
    quickColorsBaseCss() {
      if (this.rawCss && this.isAdvancedCss(this.rawCss)) {
        return this.rawCss
      }
      if (this.discardStoredStorefrontCss) {
        return ''
      }
      return (this.settings && this.settings.storefrontcss) || ''
    },
    // Hex map of the loaded base preset (defaultCssData), for success/error defaults and derive().
    quickColorsBaseTokens() {
      const parsed = this.parseCss(this.defaultCssData || '')
      const tokens = {}
      for (const key in parsed) {
        tokens[key] = parsed[key].value
      }
      return tokens
    },
    // Hex map of the shop's current tokens (cssData); derive() reads only the info colour from it.
    quickColorsCurrentTokens() {
      const tokens = {}
      for (const key in this.cssData || {}) {
        tokens[key] = this.cssData[key].value
      }
      return tokens
    },
    quickColorDefault(key) {
      if (this.quickColors && key === 'header') {
        return this.quickColors.background
      }
      if (this.quickColors && key === 'footer') {
        return this.quickColors.text
      }
      if (key === 'success' || key === 'error') {
        const tokenKey = key === 'success' ? '--johku-color-success' : '--johku-color-error'
        const fallback = key === 'success' ? '#00840f' : '#8c0000'
        return normalizeHex(this.quickColorsBaseTokens()[tokenKey]) || fallback
      }
      return '#000000'
    },
    setQuickColor(key, value, event) {
      const hex = normalizeHex(value)
      if (!hex) {
        if (event) {
          event.target.value = this.quickColors[key] || ''
        }
        return
      }
      this.quickColors = { ...this.quickColors, [key]: hex }
      this.applyQuickColors()
    },
    setQuickColorUseDefault(key, useDefault) {
      const seeds = { ...this.quickColors }
      if (useDefault) {
        delete seeds[key]
      } else {
        seeds[key] = this.quickColorDefault(key)
      }
      this.quickColors = seeds
      this.applyQuickColors()
    },
    // Write the tokens derived from the seeds over the current tokens.
    applyQuickColors() {
      const seeds = sanitizeSeeds(this.quickColors)
      if (!seeds || !this.cssData) {
        return
      }
      const derived = deriveStorefrontTokens(seeds, this.quickColorsBaseTokens(), this.quickColorsCurrentTokens())
      const next = {}
      for (const key in this.cssData) {
        next[key] = { ...this.cssData[key] }
      }
      for (const key in derived) {
        next[key] = { value: derived[key], isBasic: next[key] ? next[key].isBasic : false }
      }
      this.cssData = next
    },
    // Colors come from the base preset and the seeds; fonts, radii and other non-color tokens are kept.
    resetQuickColorsBase() {
      const base = this.sanitizeCustomCss(this.parseCss(this.defaultCssData))
      for (const key in this.cssData || {}) {
        if (!key.startsWith('--johku-color-') && key !== '--johku-footer-nav-separator') {
          base[key] = { value: this.cssData[key].value, isBasic: base[key] ? base[key].isBasic : this.cssData[key].isBasic }
        }
      }
      this.cssData = base
      this.applyQuickColors()
    },
    switchToQuickColors() {
      if (this.quickColorsMode) {
        return
      }
      // After a base-style reset the saved seeds belong to the discarded customization.
      const savedSeeds = this.discardStoredStorefrontCss
        ? null
        : sanitizeSeeds(this.settings && this.settings.storefrontcolors)
      const enter = async () => {
        if (!this.hasCustomizedSettings) {
          await this.loadDefaultCssData()
          this.cssData = this.sanitizeCustomCss(this.parseCss(this.defaultCssData))
          this.hasCustomizedSettings = true
        } else if (this.showAdvancedCssEditor) {
          // rawCss stays as it is, so its non-variable rules are kept on save.
          this.cssData = this.mergeParsedCssWithDefaults(this.sanitizeCustomCss(this.parseCss(this.stripAdvancedCssMeta(this.rawCss))))
        }
        if (this.defaultVariables !== this.loadedDefaultVariables) {
          await this.loadDefaultCssData()
        }
        this.advancedCssEditor = false
        this.quickColors = { ...(savedSeeds || seedsFromTokens(this.cssData, this.quickColorsBaseTokens())), base: presetToBase(this.defaultVariables) }
        this.quickColorsMode = true
        this.storefrontColorsFrameUnavailable = false
        this.resetQuickColorsBase()
      }
      if (this.hasCustomizedSettings && !savedSeeds) {
        this.showDialog({
          title: this._('Color selection will replace your color settings. Fonts, corner radii and other non-color settings are kept, and Advanced CSS rules are kept after the generated colors.'),
          width: 800,
          buttons: [
            {
              title: this._('Cancel'),
              icon: 'ban',
              handler: () => {}
            },
            {
              title: this._('Use color selection'),
              class: 'warning',
              icon: 'check',
              handler: enter
            }
          ],
        })
        return
      }
      return enter()
    },
    // First access: nothing saved for the custom theme (no CSS, no seeds), so there is nothing to replace.
    // hasCustomizedSettings can't tell this, because sanitising the default values on load already sets it.
    openQuickColorsOnFirstAccess() {
      if (this.quickColorsMode || !this.customThemeFirstAccess) {
        return
      }
      this.customThemeFirstAccess = false
      this.hasCustomizedSettings = false
      return this.switchToQuickColors()
    },
    // Leaving quick mode keeps the derived tokens as they are.
    leaveQuickColors(mode) {
      if (mode === 'advanced') {
        if (!this.isFeatureEnabled('gtmconsent')) {
          return
        }
        this.rawCss = this.ensureAdvancedCssSentinel(this.sanitizeAdvancedCss(buildQuickColorsCss(this.cssData, this.quickColorsBaseCss())))
        this.quickColorsMode = false
        this.switchToAdvancedCssEditor()
        return
      }
      const base = this.quickColorsBaseCss()
      const applySwitch = () => {
        this.quickColorsMode = false
        this.rawCss = ''
      }
      if (this.isFeatureEnabled('gtmconsent') && this.isAdvancedCss(base) && this.cssHasNonVariableRules(this.stripAdvancedCssMeta(base))) {
        this.showDialog({
          title: this._('Switching to variable fields will remove advanced CSS rules that are not theme variables.'),
          width: 800,
          buttons: [
            {
              title: this._('Cancel'),
              icon: 'ban',
              handler: () => {}
            },
            {
              title: this._('Switch'),
              class: 'warning',
              icon: 'check',
              handler: applySwitch
            }
          ],
        })
        return
      }
      applySwitch()
    },
    cssHasNonVariableRules(cssString) {
      if (!cssString) {
        return false
      }
      const withoutRoot = cssString.replace(/:root\s*\{[\s\S]*?\}/, '').trim()
      return withoutRoot.length > 0
    },
    sanitizeCustomCss(data) {
      for (const key in data) {
        if (data[key] && typeof data[key].value === 'string') {
          const sanitizedValue = this.sanitizeCssValue(data[key].value)
          if (data[key].value !== sanitizedValue) {
            data[key].value = sanitizedValue
          }
        }
      }
      return data
    },
    sanitizeCssValue(value) {
      if (typeof value !== 'string') return ''
      // A simple but effective way to prevent CSS injection is to remove semicolons,
      // angle brackets, and keywords like @import and url().
      return value.replace(/@import|url\(|[;<>\n\r]/gi, '')
    },
    /**
     * Parses a CSS string for variables within a :root selector.
     * @param {string} cssString - The raw CSS string.
     * @returns {Object} - An object where keys are CSS variable names and values are their properties.
     */
    parseCss(cssString) {
      if (typeof cssString !== 'string') return {}
      const cssObj = {};
      const normalized = cssString.replace(ADVANCED_CSS_SENTINEL_RE, '')
      const rootContent = normalized.match(/:root\s*\{([\s\S]*?)\}/)
      if (rootContent && rootContent[1]) {
        // Split the content by new lines to process each declaration individually.
        const lines = rootContent[1].split('\n')
        lines.forEach(line => {
          const trimmedLine = line.trim()
          // Process only lines that are CSS variable declarations.
          if (trimmedLine.startsWith('--')) {
              const firstColonIndex = trimmedLine.indexOf(':')
              if (firstColonIndex === -1) return;

              const key = trimmedLine.substring(0, firstColonIndex).trim()
              const fullValue = trimmedLine.substring(firstColonIndex + 1).trim()
              // `/* Basic */` may sit before or after the semicolon on this line.
              const isBasic = /\/\*\s*Basic\s*\*\//.test(fullValue)
              let value = fullValue.replace(/\/\*\s*Basic\s*\*\//g, '')
              value = value.replace(/(?:;\s*)+$/, '').trim()

              if (key && value) {
                  cssObj[key] = { value, isBasic }
              }
            }
          });
        }
        return cssObj
    },
    translateLabel(text) {
      switch(text) {
        case 'Link':
          return this._('noun|Link')
        case 'Orderwrap':
          return this._('Action area')
        case 'Navbar':
          return this._('Navigation bar')
        case 'Pagetabs':
          return this._('Page section tabs')
        case 'Cart':
          return this._('Basket')
        case 'Largeimage':
          return this._('Large main image')
        case 'Pricingtype':
          return this._('Hourly') + ' / ' + this._('Daily')
        case 'Input':
        case 'Hover':
        case 'Border':
        case 'Tint':
        case 'Dim':
        case 'Bar':
        case 'Transform':
        case 'Light':
        case 'Dark':
        case 'Panel':
          return this._('layout|' + text)
        case 'Titlebar':
          return this._('Title bar');
        case 'Blockshadow':
          return this._('Block shadow')
        default:
          return this._(text)
      }
    },
    /**
     * Formats a CSS variable name into a human-readable label.
     * e.g., --johku-color-warning -> Color > Warning
     * @param {string} key - The CSS variable name.
     * @returns {string} - The formatted label.
     */
    formatLabel(key) {
      return key
        .replace('--johku-', '')
        .split('-')
        .map(word => this.translateLabel(word.charAt(0).toUpperCase() + word.slice(1)))
        .join(' > ');
    },
    /**
     * Resets a specific variable to its default value.
     * @param {string} key - The CSS variable name to reset.
     */
    resetVariable(key) {
      const defaultData = this.parseCss(this.defaultCssData);
      if (defaultData[key]) {
        // Only reset the value, preserving the isBasic flag.
        this.cssData[key].value = defaultData[key].value;
      }
    },
    /**
     * Resets all variables to their default values.
     */
    resetAll() {
      this.cssData = this.parseCss(this.defaultCssData);
    },
    /**
     * Checks if a CSS variable key corresponds to a font property.
     * @param {string} key - The CSS variable name.
     * @returns {boolean}
     */
    isFont(key) {
      return key.includes('--johku-font-') && !key.includes('-transform') && !key.includes('spacing') && !key.includes('smoothing');
    },
    /**
     * Checks if a CSS variable key corresponds to a font transform property.
     * @param {string} key - The CSS variable name.
     * @returns {boolean}
     */
    isFontTransform(key) {
      return key.includes('--johku-font-') && key.includes('-transform');
    },
    /**
     * A simple check to see if a string value is likely a color.
     * @param {string} value - The CSS value.
     * @returns {boolean}
     */
    isColor(value) {
      if (typeof value !== 'string') {
        return false;
      }
      if (value.trim() === 'initial' || value.trim() === 'auto') {
        return false
      }
      const s = new Option().style;
      s.color = value;
      // Check if the browser recognizes the value as a color.
      // It's a simple check, but covers hex, rgb, rgba, hsl, and color keywords.
      console.log(s.color)
      return s.color !== '';
    },    
    removeKeyword(keyword) {
      this.searchKeywords = this.searchKeywords.filter(k => k !== keyword)
    },
    addKeyword(keyword) {
      nextTick(() => {
        this.keywordToAdd = ''
      })
      if (!keyword) {
        return
      }
      if (this.searchKeywords && this.searchKeywords.indexOf(keyword) !== -1) {
        return
      }
      if (this.settings.searchkeywords) {
        this.settings.searchkeywords = this.settings.searchkeywords + ';' + keyword
      } else {
        this.settings.searchkeywords = keyword
      }
    },
    addPageDialog () {
      this.showDialog({
        title: this._('New page'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'save',
            requiredFields: ['name'],
            handler: this.addPage
          }
        ],
        fields: [
          {
            title: this._('Page name'),
            type: 'text',
            name: 'name'
          }
        ]
      })
    },
    async addPage (data) {
      let response
      try {
        response = await axios.post(this.baseDomain + this.shopId + '/pages.json?apikey=' + this.apiKey, data)
      } catch(e) {
        this.showError(e)
        return
      }
      this.editPage(response.data, data.name, 'general')
    },
    editPage (id, name, activeTab, skipState) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'PageDetails',
        title: this._('Page') + ' ' + id + (name ? ': ' + name : ''),
        width: 6,
        data: {
          pageId: id,
          activeTab
        },
        skipState
      })
    },
    addWidgetDialog () {
      this.showDialog({
        title: this._('New widget'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'save',
            requiredFields: ['name', 'type'],
            handler: this.addWidget
          }
        ],
        fields: [
          {
            title: this._('Name'),
            type: 'text',
            name: 'name'
          },
          {
            title: this._('Type'),
            type: 'select',
            name: 'type',
            options: [
              {
                value: 'Basket',
                name: this._('Basket')
              },
              {
                value: 'Category',
                name: this._('Category')
              },
              {
                value: 'Product',
                name: this._('Product')
              },
              {
                value: 'ProductCard',
                name: this._('Product card')
              },
              {
                value: 'Search',
                name: this._('Search')
              },
              {
                value: 'Login',
                name: this._('Login')
              },
              {
                value: 'Account',
                name: this._('Account')
              },
              {
                value: 'JoinChannel',
                name: this._('Join sales channel')
              }
            ]
          }
        ]
      })
    },
    async addWidget (data) {
      let response
      try {
        response = await axios.post(this.baseDomain + this.shopId + '/widgets.json?apikey=' + this.apiKey, data)
      } catch(e) {
        this.showError(e)
        return
      }
      this.editWidget(response.data, data.name, 'general')
    },
    editWidget (id, name, activeTab, skipState) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'WidgetDetails',
        title: this._('Widget') + ' ' + id + (name ? ': ' + name : ''),
        width: 6,
        data: {
          widgetId: id,
          activeTab
        },
        skipState
      })
    },
    themeClass(index) {
      return 'theme' + (this.settings && (this.settings.shopcolorscheme + '') === (index + '') ? ' selected' : '')
    },
    setTheme(theme) {
      this.settings.shopcolorscheme = theme
    },
    getEnabledVersions(item) {
      const versionStr = item.enable_catalog
      if (versionStr === '1') {
        return this.storefrontversions
      } else if (!versionStr || versionStr === '0') {
        return []
      }
      return versionStr.split(';')
    },
    itemVersionClass(item, version) {
      const enabled = this.getEnabledVersions(item)
      return 'btn btn-xs btn-' + (enabled.indexOf(version) !== -1 ? 'success' : 'default')
    },
    toggleProductVersion(product, version) {
      this.toggleItemVersion(product, version)
      this.taintedCategories[this.selectedDisplayedCategory] = true
    },
    enableVersionForAllProducts (version) {
      let newVersions
      for (let i = 0; i < this.selectedCategoryProducts.length; i++) {
        const item = this.selectedCategoryProducts[i]
        const enabled = this.getEnabledVersions(item)
        if (enabled.indexOf(version) === -1) {
          newVersions = [...enabled]
          newVersions.push(version)
          item.enable_catalog = newVersions.join(';')
        }
      }
      this.taintedCategories[this.selectedDisplayedCategory] = true
    },
    toggleItemVersion(item, version) {
      const enabled = this.getEnabledVersions(item)
      let newVersions
      if (enabled.indexOf(version) === -1) {
        newVersions = [...enabled]
        newVersions.push(version)
      } else {
        newVersions = enabled.filter(v => v !== version)
      }
      if (!newVersions || !newVersions.length) {
        item.enable_catalog = '0'
      } else {
        item.enable_catalog = newVersions.join(';')
      }
    },
    async toggleDisplayedCategory(category) {
      this.showOtherProducts = false
      if (this.selectedDisplayedCategory === category.id) {
        this.selectedDisplayedCategory = null
        this.selectedCategoryProducts = []
      } else {
        this.selectedDisplayedCategory = category.id
        if (!this.categoryProducts[category.id]) {
          this.categoryProducts[category.id] = await this.loadCategoryProducts(category.id)
        }
        this.selectedCategoryProducts = this.categoryProducts[category.id]
        this.updateProductLists()
      }
    },
    addAllCategoriesToStorefront () {
      this.allCategories = [...this.displayedCategories, ...this.privateCategories.map(c => { 
        c.enable_catalog = this.storefrontversions.join(';')
        return c 
      })]
    },
    sortDisplayedCategories () {
      this.displayedCategories = this.sortedCategories.filter(c => c.enable_catalog)
    },
    updateProductLists () {
      this.defaultSelectedCategoryProducts = this.selectedCategoryProducts.filter(p => parseInt(p.priority) === 0)
      this.topLevelSelectedCategoryProducts = [...this.prioritySelectedCategoryProducts, { id: 'other' }, ...this.lastSelectedCategoryProducts]
    },
    displayedCategoryClass(category) {
      return 'list-group-item' + (this.selectedDisplayedCategory === category.id ? ' list-group-item-info' : '')
    },
    toggleOtherProducts () {
      this.showOtherProducts = !this.showOtherProducts
    },
    productOrderChanged (event) {
      // console.log('changed', event)
      if (event) {
        let priorityCount = this.prioritySelectedCategoryProducts.length
        // console.log('priority count', priorityCount)
        // Placeholder rows such as "No other products" are draggable but have no element
        if (event.added && event.added.element) {
          // console.log('newIndex', event.added.newIndex)
          // console.log('nextTopLevel', this.nextTopLevel)
          if (event.added.newIndex > priorityCount) { // Added to last
            // console.log('added to last')
            event.added.element.priority = -9999
          } else { // Added to priority
            // console.log('added to priority')
            event.added.element.priority = 99999
          }
        }
        if (event.removed && event.removed.element) {
          event.removed.element.priority = 0
        }
        if (event.moved && event.moved.element) {
          // console.log('newIndex', event.moved.newIndex, 'oldIndex', event.moved.oldIndex)
          if (event.moved.newIndex >= priorityCount && event.moved.oldIndex < priorityCount) {
            // console.log('moved from priority to last')
            event.moved.element.priority = -9999
          } else if (event.moved.newIndex <= priorityCount && event.moved.oldIndex > priorityCount) {
            // console.log('moved from last to priority')
            event.moved.element.priority = 9999
          }
        }
        priorityCount = this.prioritySelectedCategoryProducts.length
        let positive = priorityCount
        let negative = -1
        for (let i = 0; i < this.topLevelSelectedCategoryProducts.length; i++) {
          let el = this.topLevelSelectedCategoryProducts[i]
          if (parseInt(el.priority) > 0) {
            el.priority = positive--
          } else if (parseInt(el.priority) < 0) {
            el.priority = negative--
          }
          // console.log(el.priority, el.name)
        }
        this.selectedCategoryProducts = this.categoryProducts[this.selectedDisplayedCategory] = [...this.prioritySelectedCategoryProducts, ...this.defaultSelectedCategoryProducts.sort((a, b) => a.name > b.name ? 1 : -1), ...this.lastSelectedCategoryProducts]
        this.taintedCategories[this.selectedDisplayedCategory] = true
      }
    },
    editCategoryDialog (context) {
      this.showModal('settingscategorydetails', CategoryDetails, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          component: {
            initData: {
              categoryId: context === 'main' ? this.featuredCategory : this.selectedDisplayedCategory,
              modal: true
            }
          }
        },
        events: {
          save: async (category) => {
            if (context === 'main') {
              this.allCategoryProducts = await this.loadStorefrontCategoryProducts(category.id)
              this.updateSlideshowItems()
            } else if (context === 'products') {
              this.selectedCategoryProducts = this.categoryProducts[category.id] = await this.loadCategoryProducts(category.id)
              this.updateProductLists()
            }
          }
        },
        handlers: {},
        width: 1000
      })
    },
    selectFooterTargetProductDialog () {
      this.showModal('productselector', ProductSelector, {
        props: {
          selectedProducts: [],
          useAllProducts: false
        },
        events: {
          selectProduct: this.selectFooterTargetProduct
        },
        handlers: {},
        width: 800
      })
    },
    selectFooterTargetProduct (product) {
      this.settings.footertargetid = product.id
    },
    removeFooterTargetId () {
      this.settings.footertargetid = null
    },
    onFooterNavigationColumnsInput (event) {
      const raw = event && event.target ? event.target.value : ''
      if (raw === '' || raw === null || typeof raw === 'undefined') {
        this.settings.footernavigationcolumns = null
        return
      }
      const n = parseInt(raw, 10)
      this.settings.footernavigationcolumns = n >= 1 ? n : null
    },

    defaultFooterLinks () {
      return [
        { type: 'builtin', key: 'deliveryTerms', visible: true, names: {} },
        { type: 'builtin', key: 'privacyPolicy', visible: true, names: {} },
        { type: 'builtin', key: 'accessibilityStatement', visible: true, names: {} },
        { type: 'builtin', key: 'cookies', visible: true, names: {} }
      ]
    },
    footerLinksLocale () {
      if (this.version && this.version !== 'default') {
        return this.version
      }
      return (this.settings && this.settings.defaultversion) || 'en_US'
    },
    footerLinkNames (raw) {
      let names = raw
      if (typeof names === 'string') {
        try {
          names = JSON.parse(names)
        } catch (e) {
          names = null
        }
      }
      if (!names || typeof names !== 'object' || Array.isArray(names)) {
        return {}
      }
      const copy = {}
      Object.keys(names).forEach(locale => {
        const value = names[locale]
        if (value != null && String(value).trim() !== '') {
          copy[locale] = String(value)
        }
      })
      return copy
    },
    normalizeFooterLinks (raw) {
      let links = raw
      if (typeof links === 'string') {
        try {
          links = JSON.parse(links)
        } catch (e) {
          links = null
        }
      }
      if (!Array.isArray(links) || !links.length) {
        return this.defaultFooterLinks()
      }
      const builtinKeys = ['deliveryTerms', 'privacyPolicy', 'accessibilityStatement', 'cookies']
      const normalized = []
      const seenBuiltins = {}
      links.forEach(item => {
        if (!item || typeof item !== 'object') {
          return
        }
        if (item.type === 'product' && item.product_id) {
          normalized.push({
            type: 'product',
            product_id: String(item.product_id),
            visible: item.visible !== false && item.visible !== 0 && item.visible !== '0',
            names: this.footerLinkNames(item.names)
          })
          return
        }
        if (item.type === 'builtin' && builtinKeys.indexOf(item.key) !== -1 && !seenBuiltins[item.key]) {
          normalized.push({
            type: 'builtin',
            key: item.key,
            visible: item.visible !== false && item.visible !== 0 && item.visible !== '0',
            names: this.footerLinkNames(item.names)
          })
          seenBuiltins[item.key] = true
        }
      })
      builtinKeys.forEach(key => {
        if (!seenBuiltins[key]) {
          normalized.push({ type: 'builtin', key, visible: true, names: {} })
        }
      })
      return normalized
    },
    persistFooterLinks () {
      if (!this.footerLinks.length && this.settings && this.settings.footerlinks) {
        this.footerLinks = this.normalizeFooterLinks(this.settings.footerlinks)
      }
      if (!this.footerLinks.length) {
        return
      }
      const links = this.normalizeFooterLinks(this.footerLinks)
      this.footerLinks = links
      if (this.settings) {
        this.settings.footerlinks = links
      }
    },
    ensureFooterLinksInitialized () {
      if (!this.settings) return
      this.footerLinks = this.normalizeFooterLinks(this.settings.footerlinks)
      this.selectedFooterLinkIndex = null
    },
    footerLinkDefaultLabel (link) {
      if (!link) {
        return ''
      }
      if (link.type === 'product') {
        const product = this.allProducts.find(p => String(p.id) === String(link.product_id))
        return product ? product.name : (this._('Product') + ' ' + link.product_id)
      }
      switch (link.key) {
        case 'deliveryTerms':
          return this._('Order and cancellation terms')
        case 'privacyPolicy':
          return this._('Privacy policy')
        case 'accessibilityStatement':
          return this._('Accessibility statement')
        case 'cookies':
          return this._('Cookies')
        default:
          return link.key || ''
      }
    },
    footerLinkLabel (link) {
      const locale = this.footerLinksLocale()
      if (link && link.names && link.names[locale]) {
        return link.names[locale]
      }
      return this.footerLinkDefaultLabel(link)
    },
    footerLinkClicked (linkIndex) {
      this.selectedFooterLinkIndex = linkIndex
      this.editFooterLinkDialog()
    },
    editFooterLinkDialog () {
      if (this.selectedFooterLinkIndex === null) return
      const link = this.footerLinks[this.selectedFooterLinkIndex]
      if (!link) return
      const linkIndex = this.selectedFooterLinkIndex
      this.showModal('footerlinkeditor', FooterLinkEditor, {
        props: {
          link: Object.assign({}, link, { names: { ...(link.names || {}) } }),
          locale: this.footerLinksLocale(),
          productName: link.type === 'product' ? this.footerLinkDefaultLabel(link) : ''
        },
        events: {
          save: (payload) => {
            this.footerLinks.splice(linkIndex, 1, Object.assign({}, link, {
              visible: payload.visible !== false,
              names: payload.names || {}
            }))
            this.persistFooterLinks()
          }
        },
        handlers: {},
        width: 600
      })
    },
    addFooterProductLinkDialog () {
      this.showModal('productselector', ProductSelector, {
        props: {
          selectedProducts: [],
          useAllProducts: false
        },
        events: {
          selectProduct: (product) => {
            if (!product || !product.id) return
            this.footerLinks.push({
              type: 'product',
              product_id: String(product.id),
              visible: true,
              names: {}
            })
            this.persistFooterLinks()
            this.selectedFooterLinkIndex = this.footerLinks.length - 1
          }
        },
        handlers: {},
        width: 800
      })
    },
    removeFooterLink () {
      if (this.selectedFooterLinkIndex === null) return
      const link = this.footerLinks[this.selectedFooterLinkIndex]
      if (!link || link.type !== 'product') return
      this.footerLinks.splice(this.selectedFooterLinkIndex, 1)
      this.selectedFooterLinkIndex = null
      this.persistFooterLinks()
    },

    normalizeFeaturedCategoryGroups (raw) {
      let groups = raw
      if (typeof groups === 'string') {
        try {
          groups = JSON.parse(groups)
        } catch (e) {
          groups = null
        }
      }
      if (!Array.isArray(groups) || !groups.length) {
        const ids = this.frontpageCategories || []
        return [{
          display: 'default',
          links: ids.map(id => ({
            category_id: String(id),
            view: '',
            showdescription: 1,
            showimage: 1,
            productlimit: null,
            productlistcolumncount: null,
            title: '',
            hidetitle: 0,
            searchplaceholder: '',
            buttontext: '',
            buttonposition: '',
            showtimeresourcepool_id: null
          }))
        }]
      }
      return groups.map(g => ({
        display: (g && g.display === 'splitrows') ? 'splitrows' : 'default',
        links: Array.isArray(g && g.links) ? g.links.map(l => ({
          category_id: String(l.category_id || (l.category && l.category.id) || ''),
          view: l.view || '',
          title: l.title || '',
          hidetitle: Number(l.hidetitle) === 1 ? 1 : 0,
          showdescription: (l.showdescription === undefined || l.showdescription === null) ? 1 : (Number(l.showdescription) === 1 ? 1 : 0),
          showimage: (l.showimage === undefined || l.showimage === null) ? 1 : (Number(l.showimage) === 1 ? 1 : 0),
          productlimit: this.normalizeLinkProductLimit(l.productlimit),
          productlistcolumncount: [2, 3, 4].indexOf(parseInt(l.productlistcolumncount, 10)) !== -1 ? parseInt(l.productlistcolumncount, 10) : null,
          searchplaceholder: l.searchplaceholder || '',
          buttontext: (l.buttontext && l.buttontext !== '0') ? l.buttontext : '',
          buttonposition: l.buttonposition === 'afterdescription' ? 'afterdescription' : '',
          showtimeresourcepool_id: l.showtimeresourcepool_id || null,
          ...this.cleanLinkI18nProp(l.i18n)
        })).filter(l => l.category_id) : []
      }))
    },
    cleanLinkI18n (raw) {
      const cleaned = {}
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return cleaned
      Object.keys(raw).forEach(lang => {
        const texts = raw[lang]
        if (!texts || typeof texts !== 'object') return
        const entry = {}
        ;['title', 'buttontext', 'searchplaceholder'].forEach(field => {
          if (typeof texts[field] === 'string' && texts[field] !== '') {
            entry[field] = texts[field]
          }
        })
        if (Object.keys(entry).length) {
          cleaned[lang] = entry
        }
      })
      return cleaned
    },
    cleanLinkI18nProp (raw) {
      const cleaned = this.cleanLinkI18n(raw)
      return Object.keys(cleaned).length ? { i18n: cleaned } : {}
    },
    persistFeaturedCategoryGroups () {
      const groups = this.normalizeFeaturedCategoryGroups(this.featuredCategoryGroups)
      this.featuredCategoryGroups = groups
      this.settings.featured_category_groups = groups
      const ids = []
      groups.forEach(g => g.links.forEach(l => {
        if (l.category_id && ids.indexOf(String(l.category_id)) === -1) {
          ids.push(String(l.category_id))
        }
      }))
      const carousel = this.featuredCategory || ''
      this.settings.featured_categories = carousel + (ids.length ? ';' + ids.join(';') : '')
    },
    ensureFeaturedCategoryGroupsInitialized () {
      if (!this.settings) return
      this.featuredCategoryGroups = this.normalizeFeaturedCategoryGroups(this.settings.featured_category_groups)
      if (!this.settings.featured_category_groups) {
        this.persistFeaturedCategoryGroups()
      }
    },
    featuredCategoryGroupsReordered () {
      this.selectedFeaturedLink = null
      this.persistFeaturedCategoryGroups()
    },
    addFeaturedCategoryGroup () {
      this.featuredCategoryGroups.push({ display: 'default', links: [] })
      this.persistFeaturedCategoryGroups()
    },
    removeFeaturedCategoryGroup (groupIndex) {
      this.featuredCategoryGroups.splice(groupIndex, 1)
      if (!this.featuredCategoryGroups.length) {
        this.featuredCategoryGroups.push({ display: 'default', links: [] })
      }
      this.selectedFeaturedLink = null
      this.persistFeaturedCategoryGroups()
    },
    featuredLinkClicked (groupIndex, linkIndex) {
      if (this.selectedFeaturedLink && this.selectedFeaturedLink.groupIndex === groupIndex && this.selectedFeaturedLink.linkIndex === linkIndex) {
        this.selectedFeaturedLink = null
      } else {
        this.selectedFeaturedLink = { groupIndex, linkIndex }
      }
    },
    addFeaturedCategoryLinkDialog (groupIndex) {
      const group = this.featuredCategoryGroups[groupIndex]
      this.showModal('linkedcategoryselector', LinkedCategorySelector, {
        props: {
          frontpageMode: true,
          selectedCategories: (group.links || []).map(l => ({ id: l.category_id, category_id: l.category_id })),
          linkedCategory: null
        },
        events: {
          selectCategory: (payload) => this.addOrUpdateFeaturedCategoryLink(groupIndex, null, payload)
        },
        handlers: {},
        width: 800
      })
    },
    editFeaturedCategoryLinkDialog () {
      if (!this.selectedFeaturedLink) return
      const { groupIndex, linkIndex } = this.selectedFeaturedLink
      const group = this.featuredCategoryGroups[groupIndex]
      const link = group.links[linkIndex]
      const linkedCategory = Object.assign({}, link, { linkedcategory_id: link.category_id })
      if (!this.isDefaultVersion) {
        const texts = (link.i18n && link.i18n[this.version]) || {}
        linkedCategory.defaulttexts = {
          title: link.title || '',
          buttontext: link.buttontext || '',
          searchplaceholder: link.searchplaceholder || ''
        }
        linkedCategory.title = texts.title || ''
        linkedCategory.buttontext = texts.buttontext || ''
        linkedCategory.searchplaceholder = texts.searchplaceholder || ''
      }
      this.showModal('linkedcategoryselector', LinkedCategorySelector, {
        props: {
          frontpageMode: true,
          selectedCategories: (group.links || []).map(l => ({ id: l.category_id, category_id: l.category_id })),
          linkedCategory
        },
        events: {
          selectCategory: (payload) => this.addOrUpdateFeaturedCategoryLink(groupIndex, linkIndex, payload)
        },
        handlers: {},
        width: 800
      })
    },
    addOrUpdateFeaturedCategoryLink (groupIndex, linkIndex, payload) {
      const categoryId = payload.category_id || (payload.category && payload.category.id)
      if (!categoryId) return
      const existing = (linkIndex === null || typeof linkIndex === 'undefined') ? null : this.featuredCategoryGroups[groupIndex].links[linkIndex]
      const defaultVersion = this.isDefaultVersion
      const i18n = this.cleanLinkI18n(existing && existing.i18n)
      let texts = { title: payload.title, buttontext: payload.buttontext, searchplaceholder: payload.searchplaceholder }
      if (!defaultVersion) {
        const entry = {}
        Object.keys(texts).forEach(field => {
          if (typeof texts[field] === 'string' && texts[field] !== '' && !(field === 'buttontext' && texts[field] === '0')) {
            entry[field] = texts[field]
          }
        })
        if (Object.keys(entry).length) {
          i18n[this.version] = entry
        } else {
          delete i18n[this.version]
        }
        texts = {
          title: existing ? existing.title : '',
          buttontext: existing ? existing.buttontext : '',
          searchplaceholder: existing ? existing.searchplaceholder : ''
        }
      }
      const link = {
        category_id: String(categoryId),
        view: payload.view || '',
        title: texts.title || '',
        hidetitle: Number(payload.hidetitle) === 1 ? 1 : 0,
        showdescription: Number(payload.showdescription) === 1 ? 1 : 0,
        showimage: (payload.showimage === undefined || payload.showimage === null || payload.showimage === '' || Number(payload.showimage) === 1) ? 1 : 0,
        productlimit: this.normalizeLinkProductLimit(payload.productlimit),
        productlistcolumncount: [2, 3, 4].indexOf(parseInt(payload.productlistcolumncount, 10)) !== -1 ? parseInt(payload.productlistcolumncount, 10) : null,
        searchplaceholder: texts.searchplaceholder || '',
        buttontext: (texts.buttontext && texts.buttontext !== '0') ? texts.buttontext : '',
        buttonposition: payload.buttonposition === 'afterdescription' ? 'afterdescription' : '',
        showtimeresourcepool_id: payload.showtimeresourcepool_id || null,
        ...(Object.keys(i18n).length ? { i18n } : {})
      }
      if (linkIndex === null || typeof linkIndex === 'undefined') {
        this.featuredCategoryGroups[groupIndex].links.push(link)
      } else {
        this.featuredCategoryGroups[groupIndex].links.splice(linkIndex, 1, link)
      }
      this.persistFeaturedCategoryGroups()
    },
    removeFeaturedCategoryLink () {
      if (!this.selectedFeaturedLink) return
      const { groupIndex, linkIndex } = this.selectedFeaturedLink
      this.featuredCategoryGroups[groupIndex].links.splice(linkIndex, 1)
      this.selectedFeaturedLink = null
      this.persistFeaturedCategoryGroups()
    },

    frontpageCategoryClicked (id) {
      if (this.selectedFrontpageCategory === id) {
        this.selectedFrontpageCategory = null
      } else {
        this.selectedFrontpageCategory = id
      }
    },
    addFrontpageCategoryDialog () {
      this.showModal('categoryselector', CategorySelector, {
        props: {
          selectedCategories: this.frontpageCategories.map(c => { return { id: c } })
        },
        events: {
          selectCategory: this.addFrontpageCategory
        },
        handlers: {},
        width: 800
      })
    },
    addFrontpageCategory (category) {
      // this.frontpageCategories.push(category.id)
      this.settings.featured_categories = this.settings.featured_categories + ';' + category.id
    },
    removeFrontpageCategory () {
      if (!this.selectedFrontpageCategory) {
        return
      }
      this.frontpageCategories = this.frontpageCategories.filter(c => parseInt(c, 10) !== parseInt(this.selectedFrontpageCategory, 10))
      this.selectedFrontpageCategory = null
    },
    getSlideshowItemName (id) {
      if (id === '*') {
        return this._('images from the category')
      }
      const pid = parseInt(id, 10)
      const product = this.allCategoryProducts.find(p => parseInt(p.id, 10) === pid)
        || this.allProducts.find(p => parseInt(p.id, 10) === pid)
      return product ? product.name : ''
    },
    getCategoryName (id) {
      const category = this.allCategories.find(c => parseInt(c.id, 10) === parseInt(id, 10))
      if (category) {
        return category.name
      }
      return ''
    },
    // Puts a file uploaded through the upload queue in its place (lib/uploadaware.js).
    // The other settings images are edited in CompanyProfile.
    // The other settings images wait for CompanyProfile
    handlesUploadSlot (slot) {
      return slot === 'profile' || slot === 'storefrontlogo'
    },
    applyUploadResult (slot, file) {
      if (!this.settings || !file) {
        return
      }
      if (slot === 'profile') {
        this.settings.profile_ext = file.ext
        this.settings.profile_id = file.id
      } else if (slot === 'storefrontlogo') {
        this.settings.storefrontlogo_ext = file.ext
        this.settings.storefrontlogo_id = file.id
      }
    },
    loadCategories () {
      return new Promise(async (resolve, reject) => {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/categories.json?apikey=' + this.apiKey + this.versionStr)
        } catch(e) {
          resolve([])
          return
        }
        this.allCategories = response.data
        resolve(this.allCategories)
      })
    },
    loadProducts () {
      return new Promise(async (resolve, reject) => {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/products.json?simple=true&apikey=' + this.apiKey)
        } catch(e) {
          resolve([])
          return
        }
        this.allProducts = response.data
        resolve(this.allProducts)
      })
    },
    loadSearchTags () {
      return new Promise(async (resolve, reject) => {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/tags.json?apikey=' + this.apiKey)
        } catch(e) {
          resolve([])
          return
        }
        this.allTags = response.data
        resolve(this.allTags)
      })
    },
    loadStorefrontCategoryProducts(categoryId) {
      return this.loadCategoryProducts(categoryId, false)
    },
    loadCategoryProducts (categoryId, onlyStorefrontProducts) {
      return new Promise(async (resolve, reject) => {
        if (!categoryId) {
          resolve([])
          return
        }
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/categories/' + categoryId + '/allproducts.json?' + (onlyStorefrontProducts ? '' : 'all=true&') + 'iSortingCols=1&iSortCol_0=2&sSortDir_0=asc&apikey=' + this.apiKey)
        } catch(e) {
          // console.log(e)
          reject()
          return
        }
        resolve(response.data)
      })
    },
    updateSlideshowItems () {
      let items = []
      let doneIds = []
      let onlyStar = true
      for (let i = 0; i < this.slideshowItems.length; i++) {
        const item = this.slideshowItems[i]
        if (item === '*') {
          items.push(item)
          doneIds.push(item)
        } else {
          for (let j = 0; j < this.allCategoryProducts.length; j++) {
            const prod = this.allCategoryProducts[j].id
            if (parseInt(item, 10) === parseInt(prod, 10)) {
              onlyStar = false
              items.push(item)
              doneIds.push(item)
            }
          }
        }
      }
      if (onlyStar) {
        items = []
      }
      for (let j = 0; j < this.allCategoryProducts.length; j++) {
        const prod = this.allCategoryProducts[j].id
        if (doneIds.indexOf(prod) === -1) {
          items.push(prod)
        }
      }
      // If there was only category images (or nothing) - or the star is missing - then add the category images to the end
      if (onlyStar || !items.includes('*')) {
        items.push('*')
      }
      this.settings.slideshow_order = items.join(';')
    },
    removeTag(tag) {
      this.seoTags = this.seoTags.filter(t => t !== tag)
    },
    addTag() {
      let tag = this.newSeoTag
      if (!tag) {
        return
      }
      if (this.seoTags && this.seoTags.indexOf(tag) !== -1) {
        return
      }
      if (this.settings.seotags) {
        this.settings.seotags = this.settings.seotags + ';' + tag
      } else {
        this.settings.seotags = tag
      }
      this.newSeoTag = ''
    },
    removeImage(image) {
      if (image === 'profile') { // Remove profile image
        this.settings.profile_id = null
        this.settings.profile_ext = null
      }
      if (image === 'storefrontlogo') { // Remove storefront logo image
        this.settings.storefrontlogo_id = null
        this.settings.storefrontlogo_ext = null
      }
    },
    getDomainRows(domain) {
      const ipA = '185.26.49.31'
      const ipB = '185.26.49.32'
      let rowHtml = ''
      let parts = domain.split('.').filter(p => p !== 'www')
      var count = parts.length - 1
      if (count === 1) { // Top level
        rowHtml += '<tr><td id="rootNameA">@</td><td>3600</td><td>A</td><td>' + ipA + '</td></tr>'
        rowHtml += '<tr><td id="rootNameB">@</td><td>3600</td><td>A</td><td>' + ipB + '</td></tr>'
        rowHtml += '<tr><td id="wwwNameA">www</td><td>3600</td><td>A</td><td>' + ipA + '</td></tr>'
        rowHtml += '<tr><td id="wwwNameB">www</td><td>3600</td><td>A</td><td>' + ipB + '</td></tr>'
      } else { // Subdomain
        parts.pop()
        parts.pop()
        const name = parts.join('.')
        rowHtml += '<tr><td id="rootNameA">' + name + '</td><td>3600</td><td>A</td><td>' + ipA + '</td></tr>'
        rowHtml += '<tr><td id="rootNameB">' + name + '</td><td>3600</td><td>A</td><td>' + ipB + '</td></tr>'
        rowHtml += '<tr><td id="wwwNameA">www.' + name + '</td><td>3600</td><td>A</td><td>' + ipA + '</td></tr>'
        rowHtml += '<tr><td id="wwwNameB">www.' + name + '</td><td>3600</td><td>A</td><td>' + ipB + '</td></tr>'
      }
      return rowHtml
    },
    gdn(h) {
      return h.substring(h.lastIndexOf(".", h.lastIndexOf(".") - 1) + 1)
    },
    isValidDomain(s) {
      if (!s) {
        return true
      }
      return new RegExp('^((xn--)?[a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$', 'i').test(s)
    },
    getStorefrontCategoriesStr () {
      return this.displayedCategories.map(c => c.id + ':' + c.enable_catalog.replaceAll(';', ',')).join(';')
    },
    getStorefrontProductsStr () {
      return Object.keys(this.taintedCategories).map(k => k + ':' + this.categoryProducts[k].map(p => p.id + '=' + (p.enable_catalog ? p.enable_catalog.replaceAll(';', ',') : '0')).join(';')).join('/')
    },
    getProductPrioritiesStr () {
      return Object.keys(this.taintedCategories).map(k => k + ':' + this.categoryProducts[k].map(p => p.id + '=' + p.priority).join(';')).join('/')
    },
    getSaveData () {
      const exportFields = [
        'id', 
        'featured_categories',
        'featured_category_groups',
        'slideshow_order',
        'displaysearch',
        'fulltextsearch',
        'availabilitysearch',
        'searchcategory',
        'defaultsearchview',
        'searchmenulabel',
        'searchresultstitle',
        'searchresultssubtitle',
        'productlimit',
        'shopdescription',
        'footertargetid',
        'footertargeturl',
        'showfooterlogo',
        'hidecontacts',
        'footerlinks',
        'footernavigation',
        'footernavigationsort',
        'footernavigationcolumns',
        'footernavigationposition',
        'promotemerchantregistration',
        'profile_id',
        'storefrontlogo_id',
        'pagetitlelocation',
        'productlistcolumncount',
        'smartcrop',
        'upselltitle',
        'useacodetext',
        'marketingname',
        'shopphone',
        'shopemail',
        'hideshopfromlocal',
        'shoptitle',
        'hideshoptitle',
        'topbarstyle',
        'shopcolorscheme',
        'storefrontfontmain',
        'storefrontfonttitle',
        'storefrontfontbutton',
        'shopdomain',
        'searchdomain',
        'googlemapsapikey',
        'frontpagedescription',
        'frontpagepagetitle',
        'metadescpos',
        'metadescription',
        'analyticsapikey',
        'gtmkey',
        'googlemapsapikey',
        'googlesiteverification',
        'seotags',
        'searchkeywords',
        'cookiemanager',
        'cookiebotid',
        'customcookiemanager',
        'frontpagesearch',
        'frontpagesearchpos',
        'frontpagesearchcategory',
        'frontpageavailabilitysearch',
        'frontpagefulltextsearch',
        'frontpagesearchkeywords',
        'frontpagesearchbuttontext',
        'frontpagesearchmenulabel',
        'hidetopbarlogo',
        'storefronttype',
        'externalurl'
      ]
      const data = exportFields.reduce((a, e) => (a[e] = this.settings[e], a), {})
      data.storefrontfontmain = this.storefrontFontMain || DEFAULT_STOREFRONT_FONT
      data.storefrontfonttitle = this.storefrontFontTitle || ''
      data.storefrontfontbutton = this.storefrontFontButton || ''
      const productLimit = parseInt(this.settings.productlimit, 10)
      data.productlimit = this.isHiddenProductLimit(productLimit)
        ? this.productLimitHideSentinel
        : (productLimit > 0 ? productLimit : 0)
      data.footernavigation = this.footerNavigation
      data.footernavigationsort = this.footerNavigationSort
      const footerColumns = parseInt(this.settings.footernavigationcolumns, 10)
      data.footernavigationcolumns = footerColumns >= 1 ? footerColumns : null
      const productListColumns = parseInt(this.settings.productlistcolumncount, 10)
      data.productlistcolumncount = [2, 3, 4].indexOf(productListColumns) !== -1 ? productListColumns : null
      const hideMode = parseInt(this.settings.hideshopfromlocal, 10)
      data.hideshopfromlocal = (hideMode === 1 || hideMode === 2) ? hideMode : 0
      data.footernavigationposition = this.footerNavigationPosition
      this.persistFooterLinks()
      data.footerlinks = this.footerLinks
      data.storefront = 1
      data.storefront_categories = this.getStorefrontCategoriesStr()
      data.storefront_products = this.getStorefrontProductsStr()
      data.product_priorities = this.getProductPrioritiesStr()
      if(data.shopcolorscheme === 'variables' && this.quickColorsMode) {
        // Quick mode wins over the advanced editor state; buildQuickColorsCss keeps the Advanced CSS rules.
        data.storefrontcss = buildQuickColorsCss(this.sanitizeCustomCss(this.cssData), this.sanitizeAdvancedCss(this.quickColorsBaseCss()))
        const seeds = sanitizeSeeds(this.quickColors)
        // The base the saved tokens were derived on (applied, not just selected in the dropdown).
        data.storefrontcolors = seeds ? { base: presetToBase(this.loadedDefaultVariables || this.defaultVariables), ...seeds } : null
      } else if(data.shopcolorscheme === 'variables') {
        // The tokens are no longer derived from quick colors seeds.
        data.storefrontcolors = null
        if (this.showAdvancedCssEditor) {
          data.storefrontcss = this.ensureAdvancedCssSentinel(this.sanitizeAdvancedCss(this.rawCss || ''))
        } else if (!this.discardStoredStorefrontCss && this.settings?.storefrontcss && this.isAdvancedCss(this.settings.storefrontcss) && !this.isFeatureEnabled('gtmconsent')) {
          // Feature flag off: keep existing advanced CSS so a save does not wipe it.
          data.storefrontcss = this.settings.storefrontcss
        } else if(this.cssData) {
          data.storefrontcss = this.toCSS(this.sanitizeCustomCss(this.cssData))
        } else {
          data.storefrontcss = this.toCSS(this.sanitizeCustomCss(this.defaultCssData))
        }
      }
      data.version = this.version
      const authentikEnabled = this.isFeatureEnabled('authentik')
      data.enableFeatures = []
      data.disableFeatures = []
      if (parseInt(this.showLoginLink) && !authentikEnabled) {
        data.enableFeatures = ['authentik']
      } else if (!parseInt(this.showLoginLink) && authentikEnabled) {
        data.disableFeatures = ['authentik']
      }
      return data
    },
    parseCookiebotId () {
      if (this.settings && this.settings.cookiebotid && this.settings.cookiebotid.startsWith('<script id="Cookiebot"')) {
        let elm = document.createElement('div')
        elm.innerHTML = this.settings.cookiebotid
        let cbid = elm.firstElementChild.getAttribute('data-cbid')
        return cbid
      } else {
        return this.settings.cookiebotid
      }
    },
    async save (payment) {
      await this.awaitUploadsBeforeSave()
      this.saving = true
      if (this.settings && this.settings.cookiebotid && this.isFeatureEnabled('cookiebot')) {
        this.settings.cookiebotid = this.parseCookiebotId()
      }
      const data = this.getSaveData()
      if (data.shopdomain) {
        this.settings.shopdomain = this.settings.shopdomain.toLowerCase()
        data.shopdomain = data.shopdomain.toLowerCase()
      }
      let response
      try {
        if (data.id) {
          response = await axios.put(this.baseDomain + this.shopId + '/settings/1.json?apikey=' + this.apiKey, data)
        } else {
          this.saving = false
          this.applyPendingUploads()
          return
        }
      } catch(e) {
        this.saving = false
        this.applyPendingUploads()
        return this.showError(e)
      }
      this.saving = false
      this.saved = true
      this.syncAuthentikFeature(parseInt(this.showLoginLink))
      this.storeState()
      this.applyPendingUploads()
      setTimeout(() => { this.saved = false }, 1000)
      this.showNotification(this._('Changes saved successfully!'))
      this.$emit('save', this.settings)
      this.close()
      this.resetMenuStructure()
    },
    syncAuthentikFeature (enabled) {
      if (!this.settings) {
        return
      }
      let features = this.settings.features ? this.settings.features.split(';').filter(Boolean) : []
      if (enabled) {
        if (!features.includes('authentik')) {
          features.push('authentik')
        }
      } else {
        features = features.filter(f => f !== 'authentik')
      }
      features = [...new Set(features)].sort()
      this.settings.features = features.join(';')
    },
    async resetMenuStructure() {
      this.categoryProducts = {}
      this.taintedCategories = {}
      if (this.selectedDisplayedCategory) {
        this.categoryProducts[this.selectedDisplayedCategory] = await this.loadCategoryProducts(this.selectedDisplayedCategory)
        this.selectedCategoryProducts = this.categoryProducts[this.selectedDisplayedCategory]
        this.updateProductLists()
      }
    },
    loadVersionedSettings () {
      return new Promise(async (resolve, reject) => {
        const settings = await this.loadSettings()
        const loaders = []
        loaders.push(this.loadCategories())
        loaders.push(this.loadProducts())
        loaders.push(this.loadSearchTags())
        await Promise.allSettled(loaders)
        const featuredCategory = settings && settings.featured_categories
          ? String(settings.featured_categories).split(';')[0]
          : this.featuredCategory
        if (featuredCategory) {
          try {
            this.allCategoryProducts = await this.loadCategoryProducts(featuredCategory)
          } catch (e) {
            this.allCategoryProducts = []
          }
        }
        resolve(settings)
      })
    },
    async reloadDefaultCssData() {
      const preset = this.defaultVariables
      await this.loadDefaultCssData()
      // The request failed; loadDefaultCssData leaves the previous preset in place.
      if (this.loadedDefaultVariables !== preset) {
        this.defaultVariables = this.loadedDefaultVariables || this.defaultVariables
        return
      }
      this.hasCustomizedSettings = true
      // Every customization goes, including stored rules that are not theme variables.
      this.discardStoredStorefrontCss = true
      const presetTokens = this.sanitizeCustomCss(this.parseCss(this.defaultCssData))
      this.cssData = presetTokens
      if (this.quickColorsMode) {
        this.rawCss = ''
        this.quickColors = {
          ...seedsFromTokens(presetTokens, this.quickColorsBaseTokens()),
          base: presetToBase(preset)
        }
        this.resetQuickColorsBase()
      } else if (this.showAdvancedCssEditor) {
        this.rawCss = this.ensureAdvancedCssSentinel(this.defaultCssData || '')
      } else {
        this.rawCss = ''
        this.advancedCssEditor = false
      }
      this.showNotification(this._('Base style applied. The changes are saved only when you click Save.'))
      this.$refs.settingsContent.scrollTop = 0
    },
    loadSelectedDefaultCss() {
      if (!this.hasCustomizedSettings) {
        this.reloadDefaultCssData()
        return
      }
      let confirmed = false
      this.showDialog({
        title: this._('All custom colors, variables and custom CSS will be lost and replaced with the chosen base style. Nothing is saved until you click Save.'),
        width: 800,
        clickToClose: false,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {
              if (this.loadedDefaultVariables) {
                this.defaultVariables = this.loadedDefaultVariables
              }
            }
          },
          {
            title: this._('Reset'),
            class: 'warning',
            icon: 'check',
            handler: () => {
              confirmed = true
              this.reloadDefaultCssData()
            }
          }
        ],
        onClosed: () => {
          if (!confirmed && this.loadedDefaultVariables) {
            this.defaultVariables = this.loadedDefaultVariables
          }
        }
      })
    },
    loadDefaultCssData () {
      return new Promise(async (resolve, reject) => {
        const shopdomain = this.storefrontOrigin
        const preset = this.defaultVariables
        let response
        try {
          response = await axios.get(shopdomain + '/css/johkustore-variables-' + preset + '.css')
        } catch(e) {
          resolve('')
          return
        }
        this.defaultCssData = response.data
        this.loadedDefaultVariables = preset
        resolve(this.defaultCssData)
      })
    },
    reloadWidgetsList () {
      if (this.$refs.datatableWidgets) {
        this.$refs.datatableWidgets.reload()
      }
      return this.loadWidgetVisibility()
    },
    // Overlapping calls share one request. Only the latest request updates the list.
    loadWidgetVisibility () {
      const url = this.baseDomain + this.shopId + '/widgetvisibility.json?version=' + this.version + '&apikey=' + this.apiKey
      if (this.widgetVisibilityRequest && this.widgetVisibilityRequest.url === url) {
        return this.widgetVisibilityRequest.promise
      }
      const request = { url }
      request.promise = axios.get(url)
        .then(response => Array.isArray(response.data) ? response.data : [], () => [])
        .then((widgets) => {
          if (this.widgetVisibilityRequest !== request) {
            return
          }
          this.widgetVisibilityRequest = null
          this.affectedWidgets = widgets
          this.refreshWidgetRows()
        })
      this.widgetVisibilityRequest = request
      return request.promise
    },
    // Re-run the warning icon formatter without refetching the list.
    refreshWidgetRows () {
      const tabulator = this.$refs.datatableWidgets && this.$refs.datatableWidgets.tabulator
      if (!tabulator || tabulator.destroyed) {
        return
      }
      tabulator.getRows().forEach((row) => {
        try {
          row.reformat()
        } catch (e) {}
      })
    },
    publishTargetProducts () {
      const products = this.publishableAffectedProducts
      if (!products.length) {
        return
      }
      this.showDialog({
        title: this._('Publish the products targeted by these widgets for the current language?'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Publish products'),
            class: 'warning',
            icon: 'check',
            handler: () => this.doPublishTargetProducts(products)
          }
        ]
      })
    },
    async doPublishTargetProducts (products) {
      let failed = 0
      for (const widget of products) {
        const enableCatalog = addVersionToEnableCatalog(widget.enable_catalog, this.version)
        try {
          await axios.patch(this.baseDomain + this.shopId + '/products/' + widget.productId + '.json?apikey=' + this.apiKey, {
            id: widget.productId,
            enable_catalog: enableCatalog
          })
        } catch (e) {
          failed++
        }
      }
      if (failed) {
        this.showError(this._('Some products could not be published.'))
      } else {
        this.showNotification(this._('Products published successfully!'))
      }
      this.loadWidgetVisibility()
    },
    allowCategoriesInWidgets () {
      const categories = this.allowableAffectedCategories
      if (!categories.length) {
        return
      }
      this.showDialog({
        title: this._('Allow the categories targeted by these widgets to display in widgets?'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Allow to display in widgets'),
            class: 'warning',
            icon: 'check',
            handler: () => this.doAllowCategoriesInWidgets(categories)
          }
        ]
      })
    },
    async doAllowCategoriesInWidgets (categories) {
      let failed = 0
      for (const widget of categories) {
        try {
          await axios.patch(this.baseDomain + this.shopId + '/categories/' + widget.categoryId + '.json?apikey=' + this.apiKey, {
            id: widget.categoryId,
            enable_widgets: 1
          })
        } catch (e) {
          failed++
        }
      }
      if (failed) {
        this.showError(this._('Some categories could not be updated.'))
      } else {
        this.showNotification(this._('Categories updated successfully!'))
      }
      this.loadWidgetVisibility()
    }
  },
  components: {
    Datatable,
    Editor,
    RedirectSettings
  },
  activated () {
    if (this.component?.initData?.activateTab) {
      this.tab(this.component.initData.activateTab)
    }
  },
  async mounted () {
    this.$nextTick(() => {
      this.ensureFeaturedCategoryGroupsInitialized()
    })
    const settings = await this.loadVersionedSettings()
    const hideMode = parseInt(settings.hideshopfromlocal, 10)
    settings.hideshopfromlocal = (hideMode === 1 || hideMode === 2) ? String(hideMode) : '0'
    this.origSettings = Object.assign({}, settings)
    this.localSettings = Object.assign({}, settings)
    this.showLoginLink = this.isFeatureEnabled('authentik') ? 1 : 0
    this.defaultCssData = await this.loadDefaultCssData()
    const defaultParsed = this.parseCss(this.defaultCssData)
    if(this.settings && this.settings.storefrontcss) {
      this.hasCustomizedSettings = true
      if (this.isFeatureEnabled('gtmconsent') && this.isAdvancedCss(this.settings.storefrontcss)) {
        this.advancedCssEditor = true
        this.rawCss = this.settings.storefrontcss
        this.cssData = this.sanitizeCustomCss(this.parseCss(this.stripAdvancedCssMeta(this.settings.storefrontcss)))
      } else {
        this.cssData = this.sanitizeCustomCss(this.parseCss(this.settings.storefrontcss))
      }
    }
    const customData = this.cssData // `this.cssData` is null initially.
    const finalData = {}
    for (const key in defaultParsed) {
      if (Object.hasOwnProperty.call(defaultParsed, key)) {
          const isBasic = defaultParsed[key].isBasic
          const value = (customData && customData[key]) ? customData[key].value : defaultParsed[key].value
          finalData[key] = { value, isBasic }
      }
    }
    this.cssData = finalData
    if (!this.cssData) {
      this.cssData = this.sanitizeCustomCss(this.parseCss(this.defaultCssData))
    }
    const savedSeeds = sanitizeSeeds(this.settings && this.settings.storefrontcolors)
    this.customThemeFirstAccess = !(this.settings && this.settings.storefrontcss) && !savedSeeds
    if (this.settings && this.settings.shopcolorscheme === 'variables' && savedSeeds) {
      // rawCss keeps any Advanced CSS so its rules survive a quick mode save.
      this.advancedCssEditor = false
      this.hasCustomizedSettings = true
      // Derive on the base preset the seeds were saved with.
      this.defaultVariables = baseToPreset(savedSeeds.base)
      if (this.defaultVariables !== this.loadedDefaultVariables) {
        await this.loadDefaultCssData()
      }
      this.quickColors = savedSeeds
      this.quickColorsMode = true
      this.storefrontColorsFrameUnavailable = false
      this.resetQuickColorsBase()
    } else if (this.settings && this.settings.shopcolorscheme === 'variables') {
      // Before storeState, so the opened tab has no unsaved changes.
      await this.openQuickColorsOnFirstAccess()
    }
    this.storeState()
    this.applyPendingUploads()
    if(!this.events) {
      let events = {}
      events['widgetUpdate'] = (message) => {
        return this.reloadWidgetsList()
      }
      events['widgetVisibilityUpdate'] = (message) => {
        if (message && message.shopId && message.shopId !== this.shopId) {
          return
        }
        this.loadWidgetVisibility()
      }
      events['restoreDetails'] = (message) => {
        if (message.shopId !== this.shopId) {
          return
        }
        switch(message.component) {
          case 'PageDetails':
            this.editPage(message.data.pageId, null, null, true)
            break
        }
      }
      this.events = events
    }
    this.enableEventBus()
  }
}
</script>

<style scoped>
.advanced-css-editor {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
  line-height: 1.45;
  white-space: pre;
  tab-size: 2;
}
.theme-editor-input {
  display: flex;
  align-items: stretch;
  gap: 4px;
  .color {
    height: auto;
    border: none;
  }
  .font-select {
    flex: 1 1 auto;
    min-width: 0;
  }
}
.scheme-fonts-wrap .font-select {
  width: 100%;
}
.theme {
  cursor: pointer;
  display: block;
  border: 1px solid #DDD;
  text-align: left;
  margin-bottom: 5px;
  position: relative;
  box-shadow: 0px 0px 15px rgba(0,0,0,0.1);
}
.theme.selected:before   {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  border: 5px solid #CDDC39;
  content: '';
  pointer-events: none;
  display: block;
}
.theme:after {
  content: ".";
  visibility: hidden;
  display: block;
  height: 0;
  clear: both;
}
.theme-selector > div {
  margin-bottom: 30px;
  text-align: center;
}
.theme-color-main {
  display: block;
  height: 90px;
  background-color: #CCC;
}
.theme-color, .theme-color-1, .theme-color-2, .theme-color-3, .theme-color-4 {
  float: left;
  display: block;
  height: 30px;
  width: 25%;
  background-color: #DDD;
}
.unpublished-widgets-alert {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
}
.contrast-issues-alert {
  padding: 0;
  display: block;
}
.contrast-issues-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 15px;
  cursor: pointer;
}
.contrast-issues-bar-title {
  font-weight: bold;
  margin: 0;
}
.contrast-issues-bar-title .fa {
  margin-right: 6px;
}
.contrast-issues-toggle {
  color: inherit;
  font-weight: bold;
  text-decoration: underline;
  white-space: nowrap;
  flex-shrink: 0;
}
.contrast-issues-body {
  padding: 15px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.contrast-issues-list {
  margin: 0;
}
.contrast-issue {
  margin-bottom: 0.5em;
}
.contrast-issue:last-child {
  margin-bottom: 0;
}
.contrast-issue dd {
  margin-left: 0;
}
@media (min-width: 1200px) {
  .theme-preview-col {
    position: sticky;
    top: 10px;
  }
}
</style>